import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, APP_LOGO, APP_LOGO_WHITE } from "./lib/core";
import { AdvertCarousel } from "./components/adverts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;
const TOKEN_KEY = "topit_admin_token";

const adminApi = axios.create({ baseURL: ADMIN_API, withCredentials: true });
adminApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    // Do NOT reload for auth-bootstrap calls (login/me/logout) — the initial
    // /me returns 401 for anonymous visitors and would cause an infinite reload loop.
    const url = err?.config?.url || "";
    const isAuthCall = /\/(login|logout|me)(\?|$)/.test(url);
    if (err?.response?.status === 401 && !isAuthCall) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

const Icon = ({ name, className = "", style = {} }) => (
  <span className={`material-symbols-outlined ${className}`} style={style}>{name}</span>
);

const naira = (n) => `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

const statusColor = (s) => {
  const map = {
    "000": "bg-green-100 text-green-700", OK: "bg-green-100 text-green-700", success: "bg-green-100 text-green-700",
    operational: "bg-green-100 text-green-700",
    PENDING: "bg-amber-100 text-amber-700", pending: "bg-amber-100 text-amber-700",
    degraded: "bg-amber-100 text-amber-700", not_configured: "bg-amber-100 text-amber-700", disabled: "bg-slate-200 text-slate-600",
  };
  return map[s] || "bg-red-100 text-red-700";
};

const Badge = ({ children, color }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>{children}</span>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-20">
    <Icon name="progress_activity" className="text-red-600 text-4xl animate-spin" />
  </div>
);

// ==================== LOGIN ====================
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await adminApi.post("/login", { email, password });
      localStorage.removeItem(TOKEN_KEY); // token now lives in an httpOnly cookie
      onLogin(res.data.admin);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1220] px-4" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(225,29,46,0.15), transparent 40%)" }}>
      <div className="w-full max-w-md bg-[#111A2E] border border-white/10 rounded-3xl p-8 shadow-2xl">
        <div className="flex flex-col items-center text-center mb-1">
          <img src={APP_LOGO_WHITE} alt="TopIt" className="h-9 mx-auto" data-testid="admin-logo" />
          <span className="text-slate-400 text-[10px] tracking-wide mt-1.5" data-testid="powered-by-sineli">Admin · Powered by Sineli</span>
        </div>
        <p className="text-slate-400 text-sm mb-6">Sign in to the management portal</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-400">Email</label>
            <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full mt-1 bg-[#0B1220] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none" placeholder="admin@topit.app" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400">Password</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full mt-1 bg-[#0B1220] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none" placeholder="••••••••" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
          <button data-testid="admin-login-submit" disabled={loading} type="submit"
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Icon name="progress_activity" className="animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== SHARED UI ====================
const StatCard = ({ icon, label, value, sub, accent = "text-red-600" }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5">
    <div className="flex items-center justify-between">
      <p className="text-slate-500 text-sm">{label}</p>
      <Icon name={icon} className={accent} />
    </div>
    <p className="text-2xl font-extrabold text-slate-900 mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const Pagination = ({ page, pages, onPage }) => (
  <div className="flex items-center justify-between mt-4 text-sm">
    <span className="text-slate-500">Page {page} of {Math.max(pages, 1)}</span>
    <div className="flex gap-2">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev" className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Prev</button>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="page-next" className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Next</button>
    </div>
  </div>
);

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
        <h3 className="font-bold text-lg text-slate-900">{title}</h3>
        <button onClick={onClose} data-testid="modal-close" className="p-2 hover:bg-slate-100 rounded-lg"><Icon name="close" /></button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 focus:border-red-500 outline-none";

// ==================== OVERVIEW ====================
const OverviewPage = () => {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Spinner />;
  const maxRev = Math.max(...d.trend.map((t) => t.revenue), 1);
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="group" label="Total Customers" value={d.total_customers} sub={`${d.new_customers_today} new today`} />
        <StatCard icon="shopping_cart" label="Total Orders" value={d.total_orders} sub={`${d.orders_today} today`} accent="text-blue-600" />
        <StatCard icon="payments" label="Total Revenue" value={naira(d.total_revenue)} sub={`${naira(d.revenue_today)} today`} accent="text-green-600" />
        <StatCard icon="account_balance_wallet" label="Wallet Funded" value={naira(d.total_wallet_funded)} sub={`${naira(d.total_wallet_balance)} in wallets`} accent="text-amber-600" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="hourglass_top" label="Pending Orders" value={d.pending_orders} accent="text-amber-600" />
        <StatCard icon="error" label="Failed Orders" value={d.failed_orders} accent="text-red-600" />
        <StatCard icon="diversity_3" label="Total Referrals" value={d.total_referrals} accent="text-purple-600" />
        <StatCard icon="redeem" label="Referral Payout" value={naira(d.total_referral_payout)} accent="text-pink-600" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="font-bold text-slate-900 mb-4">Revenue & Signups (7 days)</p>
          <div className="flex items-end justify-between gap-2 h-40">
            {d.trend.map((t, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                <div className="w-full bg-red-500 rounded-t-md" style={{ height: `${Math.max((t.revenue / maxRev) * 100, 3)}%` }} title={naira(t.revenue)} />
                <span className="text-[10px] text-slate-400">{t.day}</span>
                <span className="text-[10px] font-semibold text-slate-500">{t.signups}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="font-bold text-slate-900 mb-3">Recent Orders</p>
          <div className="divide-y divide-slate-50">
            {d.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="font-semibold text-slate-700 w-16">{o.provider}</span>
                <span className="text-slate-500 flex-1 truncate">{o.phone} · {o.service_type}</span>
                <span className="font-bold text-slate-900">{naira(o.amount)}</span>
                <Badge color={statusColor(o.status_code)}>{o.status_code}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CUSTOMERS ====================
const CustomersPage = () => {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get(`/customers?search=${encodeURIComponent(search)}&status=${status}&page=${page}&limit=15`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, status, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-customers">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" className="absolute left-3 top-2.5 text-slate-400" />
          <input data-testid="customer-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search name, email, phone..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500" />
        </div>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="customer-status-filter" className="border border-slate-200 rounded-xl px-4 py-2.5 outline-none">
          <option value="all">All</option><option value="active">Active</option><option value="suspended">Suspended</option>
        </select>
      </div>
      {loading ? <Spinner /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Wallet</th><th className="px-4 py-3">Referrals</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Joined</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-red-100 text-red-700 font-bold flex items-center justify-center">{c.initials}</span>
                      <div><p className="font-semibold text-slate-900">{c.full_name || "—"}</p><p className="text-xs text-slate-400">{c.email}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold">{naira(c.wallet_balance)}</td>
                  <td className="px-4 py-3">{c.total_referrals}</td>
                  <td className="px-4 py-3"><Badge color={c.is_active ? statusColor("OK") : "bg-red-100 text-red-700"}>{c.is_active ? "Active" : "Suspended"}</Badge></td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3"><button onClick={() => setDetail(c.id)} data-testid={`view-customer-${c.id}`} className="text-red-600 font-semibold">Manage</button></td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">No customers found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={data.page} pages={data.pages} onPage={setPage} />
      {detail && <CustomerDetail userId={detail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
};

const CustomerDetail = ({ userId, onClose }) => {
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("credit");
  const [target, setTarget] = useState("main");
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(() => { adminApi.get(`/customers/${userId}`).then((r) => setD(r.data)); }, [userId]);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = async () => {
    await adminApi.patch(`/customers/${userId}`, { is_active: !d.user.is_active });
    load();
  };
  const adjust = async () => {
    if (!amount) return;
    try {
      await adminApi.post(`/customers/${userId}/adjust-balance`, { amount: Number(amount), type, target, reason: reason || "Admin adjustment" });
      setMsg("Balance updated"); setAmount(""); setReason(""); load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal title="Customer" onClose={onClose} testid="customer-detail-modal">
      {!d ? <Spinner /> : (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-red-100 text-red-700 font-bold flex items-center justify-center text-lg">{d.user.initials}</span>
            <div className="flex-1">
              <p className="font-bold text-slate-900">{d.user.full_name || "—"}</p>
              <p className="text-sm text-slate-500">{d.user.email} · {d.user.phone || "no phone"}</p>
            </div>
            <button onClick={toggleStatus} data-testid="toggle-customer-status" className={`px-3 py-2 rounded-xl font-semibold text-sm ${d.user.is_active ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
              {d.user.is_active ? "Suspend" : "Activate"}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-xs text-slate-400">Main</p><p className="font-bold">{naira(d.wallet.balance)}</p></div>
            <div className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-xs text-slate-400">Bonus</p><p className="font-bold text-amber-600">{naira(d.wallet.bonus_balance)}</p></div>
            <div className="bg-slate-50 rounded-xl p-3 text-center"><p className="text-xs text-slate-400">Cashback</p><p className="font-bold text-green-600">{naira((d.wallet.commission_balance || 0) + (d.wallet.cashback_balance || 0))}</p></div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="font-semibold text-slate-800 mb-3">Adjust Balance</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount (₦)"><input data-testid="adjust-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} /></Field>
              <Field label="Type"><select value={type} onChange={(e) => setType(e.target.value)} data-testid="adjust-type" className={inputCls}><option value="credit">Credit</option><option value="debit">Debit</option></select></Field>
              <Field label="Target"><select value={target} onChange={(e) => setTarget(e.target.value)} className={inputCls}><option value="main">Main</option><option value="bonus">Bonus</option><option value="commission">Cashback</option></select></Field>
              <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="optional" /></Field>
            </div>
            {msg && <p className="text-sm mt-2 text-slate-600">{msg}</p>}
            <button onClick={adjust} data-testid="adjust-balance-submit" className="mt-3 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2.5 rounded-xl">Apply Adjustment</button>
          </div>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Recent Orders ({d.stats.total_orders}) · Spent {naira(d.stats.total_spent)}</p>
            <div className="divide-y divide-slate-50 text-sm max-h-48 overflow-y-auto">
              {d.recent_orders.map((o) => (
                <div key={o.id} className="flex items-center gap-2 py-2">
                  <span className="font-medium text-slate-700 w-14">{o.provider}</span>
                  <span className="text-slate-500 flex-1 truncate">{o.customer_phone} · {o.service_type}</span>
                  <span className="font-bold">{naira(o.amount)}</span>
                  <Badge color={statusColor(o.status_code)}>{o.status_code}</Badge>
                </div>
              ))}
              {d.recent_orders.length === 0 && <p className="text-slate-400 py-3">No orders</p>}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ==================== ORDERS ====================
const OrdersPage = () => {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [service, setService] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get(`/orders?search=${encodeURIComponent(search)}&status=${status}&service=${service}&page=${page}&limit=15`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, status, service, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" className="absolute left-3 top-2.5 text-slate-400" />
          <input data-testid="order-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search reference, phone..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500" />
        </div>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="order-status-filter" className="border border-slate-200 rounded-xl px-4 py-2.5"><option value="all">All status</option><option value="success">Success</option><option value="pending">Pending</option><option value="failed">Failed</option></select>
        <select value={service} onChange={(e) => { setPage(1); setService(e.target.value); }} className="border border-slate-200 rounded-xl px-4 py-2.5"><option value="all">All services</option><option value="airtime">Airtime</option><option value="data">Data</option></select>
      </div>
      {loading ? <Spinner /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Provider</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {data.items.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{o.transaction_reference || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{o.provider} <span className="text-xs text-slate-400">{o.service_type}</span></td>
                  <td className="px-4 py-3">{o.customer_phone}</td>
                  <td className="px-4 py-3 font-bold">{naira(o.amount)}</td>
                  <td className="px-4 py-3"><Badge color={statusColor(o.status_code)}>{o.refunded ? "Refunded" : o.status_code}</Badge></td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(o.timestamp)}</td>
                  <td className="px-4 py-3"><button onClick={() => setDetail(o.id)} data-testid={`view-order-${o.id}`} className="text-red-600 font-semibold">View</button></td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400">No orders found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={data.page} pages={data.pages} onPage={setPage} />
      {detail && <OrderDetail orderId={detail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
};

const OrderDetail = ({ orderId, onClose }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => { adminApi.get(`/orders/${orderId}`).then((r) => setD(r.data)); }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const retry = async () => {
    setBusy("retry"); setMsg("");
    try { const r = await adminApi.post(`/orders/${orderId}/retry`); setMsg(`Status: ${r.data.status_code} — ${r.data.response_message || ""}`); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Retry failed"); } finally { setBusy(""); }
  };
  const refund = async () => {
    setBusy("refund"); setMsg("");
    try { const r = await adminApi.post(`/orders/${orderId}/refund`); setMsg(`Refunded ${naira(r.data.amount)} to customer wallet`); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Refund failed"); } finally { setBusy(""); }
  };

  return (
    <Modal title="Order Details" onClose={onClose} testid="order-detail-modal">
      {!d ? <Spinner /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-slate-400">Reference</p><p className="font-mono">{d.order.transaction_reference}</p></div>
            <div><p className="text-slate-400">Status</p><Badge color={statusColor(d.order.status_code)}>{d.order.refunded ? "Refunded" : d.order.status_code}</Badge></div>
            <div><p className="text-slate-400">Provider</p><p className="font-semibold">{d.order.provider} · {d.order.service_type}</p></div>
            <div><p className="text-slate-400">Amount</p><p className="font-bold">{naira(d.order.amount)}</p></div>
            <div><p className="text-slate-400">Phone</p><p>{d.order.customer_phone}</p></div>
            <div><p className="text-slate-400">Date</p><p>{fmtDate(d.order.timestamp)}</p></div>
            <div className="col-span-2"><p className="text-slate-400">Message</p><p>{d.order.response_message || "—"}</p></div>
            <div className="col-span-2"><p className="text-slate-400">Customer</p><p>{d.customer ? `${d.customer.full_name || ""} (${d.customer.email})` : "—"}</p></div>
          </div>
          {msg && <p className="text-sm bg-slate-50 rounded-lg p-3 text-slate-700">{msg}</p>}
          <div className="flex gap-3">
            <button onClick={retry} disabled={busy} data-testid="order-retry-btn" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
              {busy === "retry" ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="refresh" />} Re-query Status
            </button>
            <button onClick={refund} disabled={busy || d.order.refunded} data-testid="order-refund-btn" className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
              {busy === "refund" ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="undo" />} {d.order.refunded ? "Refunded" : "Refund"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ==================== TRANSACTIONS ====================
const TransactionsPage = () => {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [category, setCategory] = useState("all");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get(`/transactions?category=${category}&type=${type}&search=${encodeURIComponent(search)}&page=${page}&limit=15`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [category, type, search, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-transactions">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" className="absolute left-3 top-2.5 text-slate-400" />
          <input data-testid="tx-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search description, reference..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500" />
        </div>
        <select value={category} onChange={(e) => { setPage(1); setCategory(e.target.value); }} data-testid="tx-category-filter" className="border border-slate-200 rounded-xl px-4 py-2.5">
          <option value="all">All categories</option><option value="fund_add">Funding</option><option value="recharge">Recharge</option><option value="commission">Cashback</option><option value="referral">Referral</option><option value="refund">Refund</option><option value="admin_adjustment">Admin Adjustment</option>
        </select>
        <select value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} className="border border-slate-200 rounded-xl px-4 py-2.5"><option value="all">All</option><option value="credit">Credit</option><option value="debit">Debit</option></select>
      </div>
      {loading ? <Spinner /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Description</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Date</th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {data.items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3"><p className="font-medium text-slate-700">{t.customer_name || "—"}</p><p className="text-xs text-slate-400">{t.customer_email}</p></td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{t.description}</td>
                  <td className="px-4 py-3"><Badge color="bg-slate-100 text-slate-600">{(t.category || "").replace(/_/g, " ")}</Badge></td>
                  <td className={`px-4 py-3 font-bold ${t.type === "credit" ? "text-green-600" : "text-red-600"}`}>{t.type === "credit" ? "+" : "-"}{naira(t.amount)}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(t.timestamp)}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No transactions found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={data.page} pages={data.pages} onPage={setPage} />
    </div>
  );
};

// ==================== PRODUCTS ====================
const ProductsPage = () => {
  const [cfg, setCfg] = useState(null);
  const [providers, setProviders] = useState([]);
  const [msg, setMsg] = useState("");
  useEffect(() => { adminApi.get("/products").then((r) => { setCfg(r.data.config); setProviders(r.data.providers); }); }, []);
  const save = async () => { await adminApi.put("/products", cfg); setMsg("Saved"); setTimeout(() => setMsg(""), 2000); };
  if (!cfg) return <Spinner />;
  const toggleProvider = (p) => {
    const dis = cfg.disabled_providers || [];
    setCfg({ ...cfg, disabled_providers: dis.includes(p) ? dis.filter((x) => x !== p) : [...dis, p] });
  };
  const setDiscount = (key, val) => setCfg({ ...cfg, discounts: { ...(cfg.discounts || {}), [key]: Number(val) } });
  const disc = cfg.discounts || {};
  const PRODUCTS = [
    { key: "airtime", label: "Airtime", enable: "airtime_enabled" },
    { key: "data", label: "Data", enable: "data_enabled" },
    { key: "cable_tv", label: "Cable TV", enable: "cable_tv_enabled" },
    { key: "electricity", label: "Electricity", enable: "electricity_enabled" },
  ];
  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-products">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <p className="text-sm font-bold text-slate-700">Products — availability &amp; discount</p>
        <p className="text-xs text-slate-500 -mt-2">Toggle each product on/off in the customer menu and set an individual discount %. The customer is charged the amount minus the discount; the recipient still receives full value.</p>
        <div className="divide-y divide-slate-100">
          {PRODUCTS.map((p) => (
            <div key={p.key} className="flex items-center justify-between gap-4 py-3" data-testid={`product-row-${p.key}`}>
              <label className="flex items-center gap-3 min-w-[150px]">
                <input
                  type="checkbox"
                  checked={!!cfg[p.enable]}
                  onChange={(e) => setCfg({ ...cfg, [p.enable]: e.target.checked })}
                  data-testid={`${p.key}-enabled`}
                  className="w-5 h-5 accent-red-600"
                />
                <span className="font-semibold text-slate-700">{p.label}</span>
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Discount</span>
                <input
                  type="number" min="0" max="95" step="0.5"
                  value={disc[p.key] ?? 0}
                  onChange={(e) => setDiscount(p.key, e.target.value)}
                  data-testid={`discount-${p.key}`}
                  className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm text-right"
                />
                <span className="text-sm font-semibold text-slate-500">%</span>
              </div>
            </div>
          ))}
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2">Networks (uncheck to disable)</p>
          <div className="flex flex-wrap gap-2">
            {providers.map((p) => {
              const enabled = !(cfg.disabled_providers || []).includes(p);
              return <button key={p} onClick={() => toggleProvider(p)} data-testid={`provider-toggle-${p}`} className={`px-4 py-2 rounded-full font-semibold text-sm border ${enabled ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-400 border-slate-200"}`}>{p}</button>;
            })}
          </div>
        </div>
        <button onClick={save} data-testid="products-save" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl">Save Products Config</button>
        {msg && <span className="ml-3 text-green-600 font-semibold">{msg}</span>}
      </div>
      <p className="text-sm text-slate-400">Note: live data plans are pulled from the BillsNPay API. Cable TV &amp; Electricity toggles control whether those items appear in the customer menu (vending coming soon). Discounts apply to airtime &amp; data purchases now.</p>
    </div>
  );
};

// ==================== REFERRAL ====================
const ReferralPage = () => {
  const [cfg, setCfg] = useState(null);
  const [referrers, setReferrers] = useState({ items: [] });
  const [msg, setMsg] = useState("");
  const load = () => {
    adminApi.get("/referral/config").then((r) => setCfg(r.data));
    adminApi.get("/referral/referrers?limit=20").then((r) => setReferrers(r.data));
  };
  useEffect(() => { load(); }, []);
  const save = async () => { await adminApi.put("/referral/config", cfg); setMsg("Saved"); setTimeout(() => setMsg(""), 2000); };
  const updateTier = (i, key, val) => { const m = [...cfg.milestones]; m[i] = { ...m[i], [key]: Number(val) }; setCfg({ ...cfg, milestones: m }); };
  const addTier = () => setCfg({ ...cfg, milestones: [...cfg.milestones, { friends: 0, bonus: 0 }] });
  const removeTier = (i) => setCfg({ ...cfg, milestones: cfg.milestones.filter((_, x) => x !== i) });
  if (!cfg) return <Spinner />;
  return (
    <div className="space-y-6" data-testid="admin-referral">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 max-w-2xl">
        <p className="font-bold text-slate-900 mb-3">Referral Configuration</p>
        <Field label="Bonus per referral (₦)"><input type="number" value={cfg.bonus} onChange={(e) => setCfg({ ...cfg, bonus: Number(e.target.value) })} data-testid="referral-bonus" className={inputCls} /></Field>
        <p className="text-xs font-semibold text-slate-500 mt-4 mb-2">Milestone Tiers</p>
        <div className="space-y-2">
          {cfg.milestones.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="number" value={t.friends} onChange={(e) => updateTier(i, "friends", e.target.value)} className={inputCls} placeholder="friends" data-testid={`tier-friends-${i}`} />
              <span className="text-slate-400">→ ₦</span>
              <input type="number" value={t.bonus} onChange={(e) => updateTier(i, "bonus", e.target.value)} className={inputCls} placeholder="bonus" data-testid={`tier-bonus-${i}`} />
              <button onClick={() => removeTier(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Icon name="delete" /></button>
            </div>
          ))}
        </div>
        <button onClick={addTier} className="mt-2 text-sm text-red-600 font-semibold flex items-center gap-1"><Icon name="add" className="text-base" /> Add tier</button>
        <div className="mt-4">
          <button onClick={save} data-testid="referral-save" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl">Save Config</button>
          {msg && <span className="ml-3 text-green-600 font-semibold">{msg}</span>}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <p className="font-bold text-slate-900 px-5 pt-5">Top Referrers</p>
        <table className="w-full text-sm mt-3">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3">Referrer</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Referrals</th><th className="px-4 py-3">Milestones</th><th className="px-4 py-3">Earned</th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {referrers.items.map((r) => (
              <tr key={r.user_id}><td className="px-4 py-3"><p className="font-medium">{r.full_name || "—"}</p><p className="text-xs text-slate-400">{r.email}</p></td>
                <td className="px-4 py-3 font-mono text-xs">{r.referral_code}</td><td className="px-4 py-3 font-bold">{r.total_referrals}</td>
                <td className="px-4 py-3">{(r.milestones_claimed || []).join(", ") || "—"}</td><td className="px-4 py-3 font-semibold text-green-600">{naira(r.total_earned)}</td></tr>
            ))}
            {referrers.items.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">No referrers yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== DISCOUNTS ====================
const emptyDiscount = { type: "promo", name: "", code: "", discount_type: "percent", value: 0, service: "all", provider: "", min_amount: 0, max_discount: null, usage_limit: null, expires_at: "", is_active: true };
const DiscountsPage = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminApi.get("/discounts").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { await adminApi.delete(`/discounts/${id}`); load(); };
  return (
    <div data-testid="admin-discounts">
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-500">{items.length} discount{items.length === 1 ? "" : "s"}</p>
        <button onClick={() => setEditing(emptyDiscount)} data-testid="new-discount-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2"><Icon name="add" /> New Discount</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`discount-${d.id}`}>
            <div className="flex items-center justify-between">
              <Badge color={d.type === "promo" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>{d.type}</Badge>
              <Badge color={d.is_active ? statusColor("OK") : "bg-slate-200 text-slate-500"}>{d.is_active ? "Active" : "Inactive"}</Badge>
            </div>
            <p className="font-bold text-slate-900 mt-2">{d.name}</p>
            {d.code && <p className="font-mono text-sm text-red-600">{d.code}</p>}
            <p className="text-2xl font-extrabold text-slate-900 mt-1">{d.discount_type === "percent" ? `${d.value}%` : naira(d.value)}</p>
            <p className="text-xs text-slate-400">{d.service}{d.provider ? ` · ${d.provider}` : ""} · used {d.used_count || 0}{d.usage_limit ? `/${d.usage_limit}` : ""}</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(d)} data-testid={`edit-discount-${d.id}`} className="flex-1 border border-slate-200 rounded-lg py-1.5 text-sm font-semibold hover:bg-slate-50">Edit</button>
              <button onClick={() => remove(d.id)} data-testid={`delete-discount-${d.id}`} className="px-3 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"><Icon name="delete" className="text-base" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-400 col-span-full text-center py-10">No discounts yet. Create one to get started.</p>}
      </div>
      {editing && <DiscountModal discount={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const DiscountModal = ({ discount, onClose }) => {
  const [d, setD] = useState({ ...discount });
  const [err, setErr] = useState("");
  const isEdit = !!discount.id;
  const set = (k, v) => setD({ ...d, [k]: v });
  const save = async () => {
    try {
      const payload = { ...d, value: Number(d.value), min_amount: Number(d.min_amount) || 0,
        max_discount: d.max_discount ? Number(d.max_discount) : null, usage_limit: d.usage_limit ? Number(d.usage_limit) : null,
        expires_at: d.expires_at || null };
      if (isEdit) await adminApi.put(`/discounts/${discount.id}`, payload);
      else await adminApi.post("/discounts", payload);
      onClose();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to save"); }
  };
  return (
    <Modal title={isEdit ? "Edit Discount" : "New Discount"} onClose={onClose} testid="discount-modal">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type"><select value={d.type} onChange={(e) => set("type", e.target.value)} data-testid="discount-type" className={inputCls}><option value="promo">Promo Code</option><option value="service">Service-wide</option></select></Field>
        <Field label="Name"><input value={d.name} onChange={(e) => set("name", e.target.value)} data-testid="discount-name" className={inputCls} /></Field>
        {d.type === "promo" && <Field label="Promo Code"><input value={d.code || ""} onChange={(e) => set("code", e.target.value)} data-testid="discount-code" className={inputCls} placeholder="SAVE10" /></Field>}
        <Field label="Discount Type"><select value={d.discount_type} onChange={(e) => set("discount_type", e.target.value)} className={inputCls}><option value="percent">Percent (%)</option><option value="fixed">Fixed (₦)</option></select></Field>
        <Field label="Value"><input type="number" value={d.value} onChange={(e) => set("value", e.target.value)} data-testid="discount-value" className={inputCls} /></Field>
        <Field label="Service"><select value={d.service} onChange={(e) => set("service", e.target.value)} className={inputCls}><option value="all">All</option><option value="airtime">Airtime</option><option value="data">Data</option></select></Field>
        <Field label="Provider (optional)"><input value={d.provider || ""} onChange={(e) => set("provider", e.target.value)} className={inputCls} placeholder="MTN, Airtel..." /></Field>
        <Field label="Min amount (₦)"><input type="number" value={d.min_amount} onChange={(e) => set("min_amount", e.target.value)} className={inputCls} /></Field>
        <Field label="Max discount (₦)"><input type="number" value={d.max_discount || ""} onChange={(e) => set("max_discount", e.target.value)} className={inputCls} /></Field>
        <Field label="Usage limit"><input type="number" value={d.usage_limit || ""} onChange={(e) => set("usage_limit", e.target.value)} className={inputCls} /></Field>
        <Field label="Expires at"><input type="date" value={(d.expires_at || "").slice(0, 10)} onChange={(e) => set("expires_at", e.target.value)} className={inputCls} /></Field>
        <Field label="Active"><select value={d.is_active ? "1" : "0"} onChange={(e) => set("is_active", e.target.value === "1")} className={inputCls}><option value="1">Active</option><option value="0">Inactive</option></select></Field>
      </div>
      {err && <p className="text-red-500 text-sm mt-3">{err}</p>}
      <button onClick={save} data-testid="discount-save" className="mt-4 w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl">{isEdit ? "Update" : "Create"} Discount</button>
    </Modal>
  );
};

// ==================== CONTENT ====================
const EMAIL_PLACEHOLDERS = [
  "first_name", "otp", "recipient_email", "cta_url", "cta_text", "headline", "intro_copy",
  "highlight_label", "highlight_title", "highlight_copy", "body_copy",
  "company_name", "company_address", "current_year", "support_url", "preheader_text", "email_subject",
];

const EmailTemplatesTab = () => {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/email-templates").then((r) => {
    const list = r.data.items || [];
    setItems(list);
    if (list.length && !sel) { setSel(list[0].key); setForm({ ...list[0] }); }
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const select = (t) => { setSel(t.key); setForm({ ...t }); setPreview(""); setMsg(""); };
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/email-templates/${form.key}`, {
        name: form.name, subject: form.subject, html: form.html, is_active: form.is_active,
      });
      setMsg("Saved"); await load(); setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      // preview reflects the last SAVED version; save first so edits show
      await adminApi.put(`/email-templates/${form.key}`, { name: form.name, subject: form.subject, html: form.html, is_active: form.is_active });
      const r = await adminApi.post(`/email-templates/${form.key}/preview`);
      setPreview(r.data.html);
    } catch (e) { setMsg("Preview failed"); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { setMsg("Enter a test email"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/email-templates/${form.key}/test`, { email: testEmail });
      setMsg(`Test sent to ${testEmail}`); setTimeout(() => setMsg(""), 3000);
    } catch (e) { setMsg(e.response?.data?.detail || "Test send failed"); }
    finally { setBusy(false); }
  };

  const createNew = async () => {
    const key = window.prompt("New template key (lowercase, e.g. welcome_email):");
    if (!key) return;
    try {
      const r = await adminApi.post("/email-templates", { key, name: key });
      await load(); select(r.data);
    } catch (e) { setMsg(e.response?.data?.detail || "Create failed"); }
  };

  const del = async () => {
    if (!window.confirm(`Delete template "${form.name}"?`)) return;
    try { await adminApi.delete(`/email-templates/${form.key}`); setSel(null); setForm(null); setPreview(""); await load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Delete failed"); }
  };

  if (!items) return <Spinner />;

  return (
    <div className="space-y-4" data-testid="admin-email-templates">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Branded HTML emails with <code className="bg-slate-100 px-1 rounded">{"{{placeholders}}"}</code>. Edit, preview and send a test.</p>
        <button onClick={createNew} data-testid="email-tpl-new" className="text-sm bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2 rounded-xl flex items-center gap-1"><Icon name="add" className="text-base" /> New template</button>
      </div>

      <div className="flex flex-wrap gap-2">
        {items.map((t) => (
          <button key={t.key} onClick={() => select(t)} data-testid={`email-tpl-tab-${t.key}`}
            className={`px-3.5 py-2 rounded-xl text-sm font-semibold border transition-colors ${sel === t.key ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-200 hover:border-red-300"}`}>
            {t.name}{!t.is_active && <span className="ml-1 text-[10px] opacity-70">(off)</span>}
          </button>
        ))}
      </div>

      {form && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <Field label="Template name"><input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="email-tpl-name" className={inputCls} /></Field>
            <Field label="Subject line"><input value={form.subject || ""} onChange={(e) => set("subject", e.target.value)} data-testid="email-tpl-subject" className={inputCls} /></Field>
            <label className="flex items-center justify-between"><span className="font-semibold text-slate-700 text-sm">Active</span>
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="email-tpl-active" className="w-5 h-5 accent-red-600" /></label>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">HTML</label>
              <textarea value={form.html || ""} onChange={(e) => set("html", e.target.value)} data-testid="email-tpl-html"
                spellCheck={false} rows="16" className={`${inputCls} font-mono text-xs leading-relaxed`} />
            </div>
            <div className="text-xs text-slate-500">
              <span className="font-semibold text-slate-600">Placeholders:</span>{" "}
              {EMAIL_PLACEHOLDERS.map((p) => <code key={p} className="inline-block bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded mr-1 mb-1">{`{{${p}}}`}</code>)}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button onClick={save} disabled={busy} data-testid="email-tpl-save" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl disabled:opacity-50">Save</button>
              <button onClick={doPreview} disabled={busy} data-testid="email-tpl-preview" className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-4 py-2.5 rounded-xl">Preview</button>
              {!form.is_default && <button onClick={del} data-testid="email-tpl-delete" className="text-red-600 hover:bg-red-50 font-semibold px-4 py-2.5 rounded-xl ml-auto">Delete</button>}
            </div>
            <div className="flex items-center gap-2 border-t border-slate-100 pt-4">
              <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" data-testid="email-tpl-test-email" className={`${inputCls} flex-1`} />
              <button onClick={sendTest} disabled={busy} data-testid="email-tpl-test-send" className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-xl whitespace-nowrap">Send test</button>
            </div>
            {msg && <p className="text-sm font-semibold text-green-600" data-testid="email-tpl-msg">{msg}</p>}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Preview (sample data)</p>
            {preview
              ? <iframe title="email-preview" srcDoc={preview} data-testid="email-tpl-preview-frame" className="w-full h-[560px] rounded-xl border border-slate-200 bg-white" />
              : <div className="h-[560px] rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-sm">Click “Preview” to render this template</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// Shared admin image uploader — Object Storage upload button + reusable library + preview + URL fallback.
const resolveAdminImg = (img) => !img ? "" : (/^https?:\/\/|^data:/.test(img) ? img : `${API}${img.replace(/^\/api/, "")}`);
const AdminImageUploader = ({ value, onChange, testid, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [lib, setLib] = useState(null);
  const upload = async (fileList) => {
    const file = fileList?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await adminApi.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch (e) { alert(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const openLib = async () => {
    setLibOpen(true);
    try { const r = await adminApi.get("/uploaded-images"); setLib(r.data.images || []); }
    catch { setLib([]); }
  };
  const delImg = async (im) => {
    const used = (im.usage || []).length;
    const msg = used > 0
      ? `This image is used by ${used} item(s):\n• ${im.usage.join("\n• ")}\n\nRemove it from the library anyway? Those items keep showing it until you change them.`
      : "Remove this unused image from the library?";
    if (!window.confirm(msg)) return;
    try { await adminApi.delete(`/uploaded-images/${im.id}`); setLib((l) => (l || []).filter((x) => x.id !== im.id)); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1.5">{label}</p>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded-xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
          {resolveAdminImg(value) ? <img src={resolveAdminImg(value)} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} /> : <Icon name="image" className="text-slate-300 text-2xl" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 border border-slate-300 rounded-xl px-3 py-2 cursor-pointer hover:bg-slate-50">
              <Icon name="upload" className="text-base" /> {uploading ? "Uploading..." : "Upload"}
              <input type="file" accept="image/*" className="hidden" data-testid={`${testid}-upload`} onChange={(e) => upload(e.target.files)} />
            </label>
            <button type="button" onClick={openLib} data-testid={`${testid}-library`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 border border-slate-300 rounded-xl px-3 py-2 hover:bg-slate-50">
              <Icon name="photo_library" className="text-base" /> Library
            </button>
          </div>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-url`} className={inputCls} placeholder="…or paste image URL" />
        </div>
      </div>
      {libOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setLibOpen(false); }} data-testid={`${testid}-library-modal`}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900">Image library</h3>
              <button type="button" onClick={() => setLibOpen(false)} data-testid={`${testid}-library-close`} className="p-1.5 rounded-lg hover:bg-slate-100"><Icon name="close" /></button>
            </div>
            {lib === null ? <Spinner /> : lib.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10">No uploaded images yet. Upload one and it will appear here to reuse.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {lib.map((im) => (
                  <div key={im.id} className="relative group aspect-square">
                    <button type="button" data-testid={`${testid}-lib-item`} title={(im.usage && im.usage.length) ? `Used by: ${im.usage.join(", ")}` : (im.filename || "")}
                      onClick={() => { onChange(im.url); setLibOpen(false); }}
                      className="w-full h-full rounded-xl border border-slate-200 overflow-hidden bg-slate-50 hover:ring-2 hover:ring-red-500 transition-all">
                      <img src={resolveAdminImg(im.url)} alt={im.filename || ""} className="w-full h-full object-cover" />
                    </button>
                    <span className={`absolute bottom-1 left-1 right-1 text-[9px] font-bold text-white rounded px-1 py-0.5 truncate pointer-events-none ${(im.usage && im.usage.length) ? "bg-slate-900/75" : "bg-green-600/85"}`} data-testid={`${testid}-lib-usage`}>
                      {(im.usage && im.usage.length) ? `In use · ${im.usage.length}` : "Unused"}
                    </span>
                    <button type="button" data-testid={`${testid}-lib-delete`} title="Remove from library"
                      onClick={(e) => { e.stopPropagation(); delImg(im); }}
                      className="absolute top-1 right-1 w-7 h-7 rounded-lg bg-white/90 text-red-600 border border-slate-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50">
                      <Icon name="delete" className="text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Live phone-style preview of the onboarding carousel (enabled slides only).
const renderPreviewTitle = (title, highlight) => {
  const t = title || ""; const h = (highlight || "").trim();
  const idx = h ? t.toLowerCase().indexOf(h.toLowerCase()) : -1;
  if (idx === -1) return t;
  return <>{t.slice(0, idx)}<span className="text-red-600">{t.slice(idx, idx + h.length)}</span>{t.slice(idx + h.length)}</>;
};
const OnboardingPreview = ({ slides }) => {
  const enabled = (slides || []).filter((s) => s.enabled !== false);
  const [i, setI] = useState(0);
  useEffect(() => { if (i > enabled.length - 1) setI(0); }, [enabled.length, i]);
  useEffect(() => {
    if (enabled.length <= 1) return;
    const t = setTimeout(() => setI((x) => (x + 1) % enabled.length), 3500);
    return () => clearTimeout(t);
  }, [i, enabled.length]);
  const cur = enabled[i] || {};
  return (
    <div className="sticky top-4" data-testid="onboarding-preview">
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2 text-center lg:text-left">Live preview</p>
      <div className="mx-auto w-[260px] rounded-[2.2rem] border-[10px] border-slate-900 bg-white shadow-xl overflow-hidden" style={{ height: "540px" }}>
        {enabled.length === 0 ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm px-6 text-center">All slides hidden — tick “Show” on a slide to preview it.</div>
        ) : (
          <div key={i} className="h-full flex flex-col items-center px-5 pt-8 pb-5 text-center animate-fadeIn">
            <img src={APP_LOGO} alt="TopIt" className="h-12 object-contain" />
            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-1.5 tracking-wider text-[9px] font-bold">
              {(cur.eyebrow || []).map((w, k) => <span key={k} className={k === 1 ? "text-red-600" : "text-slate-900"}>{w}</span>)}
            </div>
            <h3 className="text-slate-900 text-base font-bold leading-tight mt-3">{renderPreviewTitle(cur.title, cur.highlight)}</h3>
            <p className="text-slate-500 text-[11px] mt-1.5 leading-snug line-clamp-3">{cur.subtitle}</p>
            <div className="flex-1 flex items-center justify-center my-2 min-h-0">
              {resolveAdminImg(cur.image)
                ? <img src={resolveAdminImg(cur.image)} alt="" className="max-w-[150px] max-h-[150px] object-contain drop-shadow-lg" />
                : <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center"><Icon name="image" className="text-slate-300 text-3xl" /></div>}
            </div>
            <div className="flex items-center justify-center gap-1.5 mb-2.5">
              {enabled.map((_, k) => <span key={k} className={`rounded-full transition-all ${k === i ? "w-5 h-1.5 bg-red-600" : "w-1.5 h-1.5 bg-slate-300"}`} />)}
            </div>
            <div className="w-full bg-red-600 text-white text-xs font-semibold py-2.5 rounded-xl">Get Started</div>
          </div>
        )}
      </div>
    </div>
  );
};

const OnboardingPage = () => {
  const [slides, setSlides] = useState(null);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(null);
  useEffect(() => { adminApi.get("/landing-slides").then((r) => setSlides(r.data.slides || [])); }, []);
  const save = async () => {
    await adminApi.put("/landing-slides", { slides });
    setMsg("Saved"); setTimeout(() => setMsg(""), 2000);
  };
  if (!slides) return <Spinner />;
  const upd = (i, patch) => setSlides(slides.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const updEyebrow = (i, k, val) => { const eb = [...(slides[i].eyebrow || ["", "", ""])]; eb[k] = val; upd(i, { eyebrow: eb }); };
  const remove = (i) => setSlides(slides.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= slides.length) return;
    const arr = [...slides]; [arr[i], arr[j]] = [arr[j], arr[i]]; setSlides(arr);
  };
  const add = () => setSlides([...slides, { eyebrow: ["", "", ""], title: "New slide title", highlight: "", subtitle: "", image: "", enabled: true }]);
  const imgSrc = (img) => !img ? "" : (/^https?:\/\/|^data:/.test(img) ? img : `${API}${img.replace(/^\/api/, "")}`);
  const uploadImage = async (i, fileList) => {
    const file = fileList?.[0]; if (!file) return;
    setUploading(i);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await adminApi.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(i, { image: r.data.url });
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };
  return (
    <div className="grid lg:grid-cols-[260px,1fr] gap-6 items-start" data-testid="admin-onboarding">
      <OnboardingPreview slides={slides} />
      <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="text-sm font-bold text-slate-700">Onboarding carousel</p>
        <p className="text-xs text-slate-500 mt-1">Edit the slides shown on the welcome/landing screen. The <span className="font-semibold text-red-600">highlight</span> text is coloured red inside the title. The middle eyebrow word is red. Each slide can have its own image (leave empty to use the default illustration). Untick "Show" to hide a slide.</p>
      </div>
      {slides.map((s, i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid={`slide-card-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Slide {i + 1}</span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                <input type="checkbox" checked={s.enabled !== false} onChange={(e) => upd(i, { enabled: e.target.checked })} data-testid={`slide-enabled-${i}`} className="w-4 h-4 accent-red-600" /> Show
              </label>
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`slide-up-${i}`} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30"><Icon name="arrow_upward" className="text-sm" /></button>
              <button onClick={() => move(i, 1)} disabled={i === slides.length - 1} data-testid={`slide-down-${i}`} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30"><Icon name="arrow_downward" className="text-sm" /></button>
              <button onClick={() => remove(i)} data-testid={`slide-remove-${i}`} className="w-8 h-8 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Icon name="delete" className="text-sm" /></button>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {imgSrc(s.image) ? <img src={imgSrc(s.image)} alt="" className="w-full h-full object-contain" data-testid={`slide-image-preview-${i}`} /> : <Icon name="image" className="text-slate-300 text-2xl" />}
            </div>
            <div className="flex-1 space-y-2">
              <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 border border-slate-300 rounded-xl px-3 py-2 cursor-pointer hover:bg-slate-50">
                <Icon name="upload" className="text-base" /> {uploading === i ? "Uploading..." : "Upload image"}
                <input type="file" accept="image/*" className="hidden" data-testid={`slide-image-upload-${i}`} onChange={(e) => uploadImage(i, e.target.files)} />
              </label>
              <Field label="…or image URL"><input value={s.image || ""} onChange={(e) => upd(i, { image: e.target.value })} data-testid={`slide-image-url-${i}`} className={inputCls} placeholder="https://... (leave empty for default)" /></Field>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">Eyebrow (3 words — middle is red)</p>
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map((k) => (
                <input key={k} value={(s.eyebrow || [])[k] || ""} onChange={(e) => updEyebrow(i, k, e.target.value)} data-testid={`slide-eyebrow-${i}-${k}`} className={`${inputCls} ${k === 1 ? "text-red-600 font-semibold" : ""}`} placeholder={k === 1 ? "RED WORD" : "WORD"} />
              ))}
            </div>
          </div>
          <Field label="Title"><input value={s.title || ""} onChange={(e) => upd(i, { title: e.target.value })} data-testid={`slide-title-${i}`} className={inputCls} /></Field>
          <Field label="Highlight (part of the title to colour red)"><input value={s.highlight || ""} onChange={(e) => upd(i, { highlight: e.target.value })} data-testid={`slide-highlight-${i}`} className={inputCls} placeholder="e.g. 5% cashback" /></Field>
          <Field label="Subtitle"><textarea value={s.subtitle || ""} onChange={(e) => upd(i, { subtitle: e.target.value })} data-testid={`slide-subtitle-${i}`} rows={2} className={inputCls} /></Field>
        </div>
      ))}
      <div className="flex items-center gap-3">
        <button onClick={add} data-testid="slide-add" className="border border-slate-300 text-slate-700 font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-50 flex items-center gap-1.5"><Icon name="add" className="text-base" /> Add slide</button>
        <button onClick={save} data-testid="onboarding-save" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl">Save Onboarding</button>
        {msg && <span className="text-green-600 font-semibold">{msg}</span>}
      </div>
      </div>
    </div>
  );
};


const CarouselPreview = ({ list, interval, variant }) => {
  const [device, setDevice] = useState("phone");
  const ads = (list || [])
    .filter((a) => a && (a.image || a.title || a.text))
    .map((a) => ({ ...a, image: a.image ? resolveAdminImg(a.image) : "" }));
  if (ads.length === 0) return (
    <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400" data-testid={`${variant}-preview-empty`}>
      Add a slide with an image or text to see a live preview of how it looks to customers.
    </div>
  );
  if (variant === "panel") {
    return (
      <div data-testid="advert-preview-panel">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Live preview — desktop side panel</p>
        <div className="flex justify-center bg-slate-100 rounded-2xl p-4 overflow-hidden">
          <div style={{ width: 232, height: 432, overflow: "hidden" }}>
            <div style={{ transform: "scale(0.6)", transformOrigin: "top left" }}>
              <AdvertCarousel ads={ads} interval={interval} variant="panel" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  const w = device === "phone" ? 360 : 640;
  return (
    <div data-testid="advert-preview-banner">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Live preview</p>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          <button type="button" onClick={() => setDevice("phone")} data-testid="advert-preview-phone" className={`px-2.5 py-1 rounded-md text-xs font-semibold ${device === "phone" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Phone</button>
          <button type="button" onClick={() => setDevice("desktop")} data-testid="advert-preview-desktop" className={`px-2.5 py-1 rounded-md text-xs font-semibold ${device === "desktop" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Desktop</button>
        </div>
      </div>
      <div className="flex justify-center bg-slate-100 rounded-2xl p-4">
        <div style={{ width: w, maxWidth: "100%" }}>
          <AdvertCarousel ads={ads} interval={interval} variant="banner" />
        </div>
      </div>
    </div>
  );
};

const ContentPage = () => {
  const [ptab, setPtab] = useState("general");
  const [c, setC] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    adminApi.get("/content").then((r) => {
      const d = r.data || {};
      if (!Array.isArray(d.adverts)) {
        d.adverts = (d.advert_image || d.advert_title || d.advert_text)
          ? [{ image: d.advert_image || "", title: d.advert_title || "", text: d.advert_text || "", link: d.advert_link || "", cta: d.advert_cta || "Learn more" }]
          : [];
      }
      if (!Array.isArray(d.banner_adverts)) d.banner_adverts = [];
      if (d.advert_interval == null) d.advert_interval = 6;
      if (d.banner_advert_interval == null) d.banner_advert_interval = 6;
      if (d.banner_advert_enabled == null) d.banner_advert_enabled = false;
      setC(d);
    });
  }, []);
  const save = async () => { await adminApi.put("/content", c); setMsg("Saved"); setTimeout(() => setMsg(""), 2000); };
  if (!c) return <Spinner />;
  const set = (k, v) => setC({ ...c, [k]: v });

  // Reusable advert-carousel manager (used for both the desktop side panel and the mobile banner).
  const advertManager = ({ title, sub, enabledKey, intervalKey, listKey, prefix }) => {
    const list = c[listKey] || [];
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid={`admin-${prefix}`}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Enabled
            <input type="checkbox" checked={!!c[enabledKey]} onChange={(e) => set(enabledKey, e.target.checked)} data-testid={`${prefix}-enabled-toggle`} className="w-5 h-5 accent-red-600" /></label>
        </div>
        <p className="text-xs text-slate-400 -mt-2">{sub}</p>
        <Field label="Transition interval (seconds per slide)">
          <input type="number" min="2" value={c[intervalKey] ?? 6}
            onChange={(e) => set(intervalKey, Math.max(2, Number(e.target.value) || 2))}
            data-testid={`${prefix}-interval-input`} className={inputCls} />
        </Field>
        <CarouselPreview list={list} interval={c[intervalKey]} variant={listKey === "banner_adverts" ? "banner" : "panel"} />
        <div className="space-y-4">
          {list.map((adv, i) => {
            const upd = (k, v) => set(listKey, list.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
            const remove = () => set(listKey, list.filter((_, idx) => idx !== i));
            const move = (dir) => {
              const arr = [...list]; const j = i + dir;
              if (j < 0 || j >= arr.length) return;
              [arr[i], arr[j]] = [arr[j], arr[i]]; set(listKey, arr);
            };
            return (
              <div key={i} className="rounded-xl border border-slate-200 p-4 space-y-3 relative bg-slate-50/60" data-testid={`${prefix}-item-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Slide {i + 1}</span>
                    {(() => {
                      const today = new Date().toISOString().slice(0, 10);
                      let st = null;
                      if (adv.starts_at && adv.starts_at > today) st = { label: "Scheduled", cls: "bg-amber-100 text-amber-700" };
                      else if (adv.ends_at && adv.ends_at < today) st = { label: "Expired", cls: "bg-red-100 text-red-600" };
                      else if (adv.starts_at || adv.ends_at) st = { label: "Live", cls: "bg-green-100 text-green-700" };
                      return st ? <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`${prefix}-status-${i}`}>{st.label}</span> : null;
                    })()}
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => move(-1)} disabled={i === 0} data-testid={`${prefix}-up-${i}`} className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30"><Icon name="arrow_upward" className="text-base" /></button>
                    <button type="button" onClick={() => move(1)} disabled={i === list.length - 1} data-testid={`${prefix}-down-${i}`} className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30"><Icon name="arrow_downward" className="text-base" /></button>
                    <button type="button" onClick={remove} data-testid={`${prefix}-remove-${i}`} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Icon name="delete" className="text-base" /></button>
                  </div>
                </div>
                <AdminImageUploader value={adv.image} onChange={(v) => upd("image", v)} testid={`${prefix}-image-${i}`} label="Advert image" />
                <Field label="Image fit">
                  <select value={adv.fit || "cover"} onChange={(e) => upd("fit", e.target.value)} data-testid={`${prefix}-fit-${i}`} className={inputCls}>
                    <option value="cover">Fill — crop to fill the frame (no gaps)</option>
                    <option value="contain">Fit — show the whole image (may letterbox)</option>
                  </select>
                </Field>
                <Field label="Title"><input value={adv.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid={`${prefix}-title-${i}`} className={inputCls} /></Field>
                <Field label="Text"><textarea value={adv.text || ""} onChange={(e) => upd("text", e.target.value)} data-testid={`${prefix}-text-${i}`} className={inputCls} rows="2" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Link (URL)"><input value={adv.link || ""} onChange={(e) => upd("link", e.target.value)} data-testid={`${prefix}-link-${i}`} placeholder="https://..." className={inputCls} /></Field>
                  <Field label="Button label"><input value={adv.cta || ""} onChange={(e) => upd("cta", e.target.value)} data-testid={`${prefix}-cta-${i}`} placeholder="Learn more" className={inputCls} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Starts on (optional)"><input type="date" value={(adv.starts_at || "").slice(0, 10)} onChange={(e) => upd("starts_at", e.target.value)} data-testid={`${prefix}-starts-${i}`} className={inputCls} /></Field>
                  <Field label="Ends on (optional)"><input type="date" value={(adv.ends_at || "").slice(0, 10)} onChange={(e) => upd("ends_at", e.target.value)} data-testid={`${prefix}-ends-${i}`} className={inputCls} /></Field>
                </div>
                <p className="text-[11px] text-slate-400 -mt-1">Leave dates empty to run this slide always. Scheduled slides appear/expire automatically for customers.</p>
              </div>
            );
          })}
          {list.length === 0 && <p className="text-sm text-slate-400 italic">No slides yet. Add one below.</p>}
        </div>
        <button type="button" onClick={() => set(listKey, [...list, { image: "", title: "", text: "", link: "", cta: "Learn more", fit: "cover", starts_at: "", ends_at: "" }])}
          data-testid={`${prefix}-add`} className="w-full border-2 border-dashed border-slate-300 hover:border-red-400 hover:text-red-600 text-slate-500 font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2">
          <Icon name="add" /> Add advert slide
        </button>
        <button onClick={save} data-testid={`${prefix}-save`} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl">Save Adverts</button>
        {msg && <span className="ml-3 text-green-600 font-semibold">{msg}</span>}
      </div>
    );
  };

  const tabCls = (t) => `px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${ptab === t ? "bg-red-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-red-300"}`;

  return (
    <div className={ptab === "email" ? "max-w-6xl space-y-5" : ptab === "onboarding" ? "max-w-5xl space-y-5" : "max-w-2xl space-y-5"} data-testid="admin-content">
      <div className="flex gap-2">
        <button onClick={() => setPtab("general")} data-testid="content-tab-general" className={tabCls("general")}>General</button>
        <button onClick={() => setPtab("onboarding")} data-testid="content-tab-onboarding" className={tabCls("onboarding")}>Onboarding</button>
        <button onClick={() => setPtab("email")} data-testid="content-tab-email" className={tabCls("email")}>Email Templates</button>
      </div>
      {ptab === "email" ? <EmailTemplatesTab /> : ptab === "onboarding" ? <OnboardingPage /> : (
      <>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <Field label="Landing hero title"><input value={c.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} data-testid="content-hero-title" className={inputCls} /></Field>
        <Field label="Landing hero subtitle"><textarea value={c.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className={inputCls} rows="2" /></Field>
        <Field label="Trust badge text"><input value={c.trust_text || ""} onChange={(e) => set("trust_text", e.target.value)} className={inputCls} /></Field>
        <Field label="Dashboard promo banner"><input value={c.promo_banner || ""} onChange={(e) => set("promo_banner", e.target.value)} data-testid="content-promo" className={inputCls} /></Field>
        <p className="text-xs text-slate-400">Cable TV &amp; Electricity availability now lives on the <span className="font-semibold text-slate-500">Products</span> page.</p>
        <button onClick={save} data-testid="content-save" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl">Save Content</button>
        {msg && <span className="ml-3 text-green-600 font-semibold">{msg}</span>}
      </div>

      {advertManager({
        title: "Desktop Side-Panel Carousel",
        sub: "Slides shown on the right side of the dashboard on wide screens (≥16\"). Rotate automatically.",
        enabledKey: "advert_enabled", intervalKey: "advert_interval", listKey: "adverts", prefix: "advert",
      })}

      {advertManager({
        title: "Mobile / ≤16\" Banner Carousel",
        sub: "Full-width banner shown in place of \"Top Network Providers\" on mobile & screens ≤16\". Managed independently from the side panel.",
        enabledKey: "banner_advert_enabled", intervalKey: "banner_advert_interval", listKey: "banner_adverts", prefix: "banner-advert",
      })}
      </>
      )}
    </div>
  );
};

// ==================== OFFERS / DISCOVERY ====================
const OfferCardPreview = ({ o }) => {
  const [device, setDevice] = useState("phone");
  const accent = o.accent || "#E11D2E";
  const img = o.image ? resolveAdminImg(o.image) : "";
  const w = device === "phone" ? 280 : 360;
  return (
    <div data-testid="offer-preview">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Live preview</p>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          <button type="button" onClick={() => setDevice("phone")} data-testid="offer-preview-phone" className={`px-2.5 py-1 rounded-md text-xs font-semibold ${device === "phone" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Phone</button>
          <button type="button" onClick={() => setDevice("desktop")} data-testid="offer-preview-desktop" className={`px-2.5 py-1 rounded-md text-xs font-semibold ${device === "desktop" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Desktop</button>
        </div>
      </div>
      <div className="flex justify-center bg-slate-100 rounded-2xl p-4">
        <div className="bg-white rounded-3xl border border-slate-200/70 overflow-hidden flex flex-col" style={{ width: w, maxWidth: "100%" }}>
          <div className="relative h-32 overflow-hidden" style={{ background: accent + "18" }}>
            {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-5xl">{o.emoji || "🎁"}</div>}
            <span className="absolute top-3 left-3 text-[10px] font-bold text-white px-2.5 py-1 rounded-full backdrop-blur" style={{ background: accent + "cc" }}>{o.category || "Offer"}</span>
          </div>
          <div className="p-4 flex-1 flex flex-col">
            <div className="flex items-center gap-1.5">
              {o.emoji && <span className="text-base">{o.emoji}</span>}
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: accent }}>{o.brand || "BRAND"}</p>
            </div>
            <p className="font-extrabold text-slate-900 mt-1 leading-tight">{o.title || "Offer title"}</p>
            <p className="text-sm text-slate-500 mt-0.5 flex-1">{o.subtitle || "Add a subtitle to describe the deal."}</p>
            <div className="mt-3 w-full text-center text-sm font-bold text-white py-2.5 rounded-xl" style={{ background: accent }}>{o.cta || "Claim offer"}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
const OFFER_CATEGORIES = ["Food", "Transport", "Entertainment", "Travel", "Shopping", "Lifestyle", "General"];
const OffersPage = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => adminApi.get("/offers").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const blank = { brand: "", title: "", subtitle: "", category: "Food", cta: "Claim offer", accent: "#E11D2E", emoji: "", image: "", link: "", is_active: true, starts_at: "", ends_at: "", sort_order: (items?.length || 0) };

  const save = async () => {
    const o = editing;
    if (!o.brand?.trim() || !o.title?.trim()) { setMsg("Brand and title are required"); return; }
    try {
      if (o.id) await adminApi.put(`/offers/${o.id}`, o);
      else await adminApi.post("/offers", o);
      setEditing(null); setMsg("Saved"); setTimeout(() => setMsg(""), 2000); load();
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete offer "${o.title}"?`)) return;
    await adminApi.delete(`/offers/${o.id}`); load();
  };

  const toggle = async (o) => { await adminApi.put(`/offers/${o.id}`, { is_active: !o.is_active }); load(); };

  if (!items) return <Spinner />;
  const set = (k, v) => setEditing({ ...editing, [k]: v });
  const offerStatus = (o) => {
    const today = new Date().toISOString().slice(0, 10);
    if (!o.is_active) return { label: "Hidden", cls: "bg-slate-100 text-slate-500" };
    if (o.starts_at && o.starts_at > today) return { label: "Scheduled", cls: "bg-amber-100 text-amber-700" };
    if (o.ends_at && o.ends_at < today) return { label: "Expired", cls: "bg-red-100 text-red-600" };
    return { label: "Live", cls: "bg-green-100 text-green-700" };
  };

  return (
    <div className="max-w-4xl space-y-5" data-testid="admin-offers">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Offers & Discovery</h2>
          <p className="text-sm text-slate-400">Manage the brand deals shown on the customer Discover page & home carousel.</p>
        </div>
        <button onClick={() => setEditing(blank)} data-testid="offer-add" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2.5 rounded-xl flex items-center gap-1.5"><Icon name="add" />New offer</button>
      </div>
      {msg && <div className="text-green-600 font-semibold text-sm">{msg}</div>}

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400" data-testid="offers-empty">
          No offers yet. The customer app shows built-in defaults until you add your own.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`offer-row-${o.id}`}>
              <div className="h-24 bg-slate-100 flex items-center justify-center overflow-hidden">
                {o.image ? <img src={o.image} alt={o.brand} className="w-full h-full object-cover" /> : <span className="text-4xl">{o.emoji || "🎁"}</span>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase" style={{ color: o.accent || "#E11D2E" }}>{o.brand}</span>
                  {(() => { const st = offerStatus(o); return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`offer-status-${o.id}`}>{st.label}</span>; })()}
                </div>
                <p className="font-bold text-slate-900 mt-1">{o.title}</p>
                <p className="text-sm text-slate-500">{o.subtitle}</p>
                {(o.starts_at || o.ends_at) && (
                  <p className="text-[11px] text-slate-400 mt-1" data-testid={`offer-schedule-${o.id}`}>
                    <Icon name="event" className="text-xs align-middle mr-0.5" />
                    {o.starts_at ? `From ${o.starts_at}` : "Now"}{o.ends_at ? ` → ${o.ends_at}` : ""}
                  </p>
                )}
                <span className="inline-block text-[10px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full mt-2">{o.category}</span>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(o)} data-testid={`offer-edit-${o.id}`} className="flex-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-lg">Edit</button>
                  <button onClick={() => toggle(o)} data-testid={`offer-toggle-${o.id}`} className="flex-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-lg">{o.is_active ? "Hide" : "Show"}</button>
                  <button onClick={() => remove(o)} data-testid={`offer-delete-${o.id}`} className="px-3 text-red-600 hover:bg-red-50 rounded-lg"><Icon name="delete" className="text-base" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="offer-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-4">{editing.id ? "Edit offer" : "New offer"}</h3>
            <div className="mb-4"><OfferCardPreview o={editing} /></div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand"><input value={editing.brand || ""} onChange={(e) => set("brand", e.target.value)} data-testid="offer-brand" className={inputCls} /></Field>
              <Field label="Category">
                <select value={editing.category || "General"} onChange={(e) => set("category", e.target.value)} data-testid="offer-category" className={inputCls}>
                  {OFFER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Title"><input value={editing.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="offer-title" className={inputCls} /></Field>
            <Field label="Subtitle"><input value={editing.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} data-testid="offer-subtitle" className={inputCls} /></Field>
            <div className="mb-3"><AdminImageUploader value={editing.image} onChange={(v) => set("image", v)} testid="offer-image" label="Offer image" /></div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Emoji"><input value={editing.emoji || ""} onChange={(e) => set("emoji", e.target.value)} data-testid="offer-emoji" placeholder="🍔" className={inputCls} /></Field>
              <Field label="CTA label"><input value={editing.cta || ""} onChange={(e) => set("cta", e.target.value)} data-testid="offer-cta" className={inputCls} /></Field>
              <Field label="Accent"><input type="color" value={editing.accent || "#E11D2E"} onChange={(e) => set("accent", e.target.value)} data-testid="offer-accent" className="w-full h-10 rounded-lg border border-slate-200" /></Field>
            </div>
            <Field label="Link (optional — opens on claim)"><input value={editing.link || ""} onChange={(e) => set("link", e.target.value)} data-testid="offer-link" placeholder="https://..." className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts on (optional)"><input type="date" value={(editing.starts_at || "").slice(0, 10)} onChange={(e) => set("starts_at", e.target.value)} data-testid="offer-starts" className={inputCls} /></Field>
              <Field label="Ends on (optional)"><input type="date" value={(editing.ends_at || "").slice(0, 10)} onChange={(e) => set("ends_at", e.target.value)} data-testid="offer-ends" className={inputCls} /></Field>
            </div>
            <p className="text-[11px] text-slate-400 -mt-1">Leave dates empty to show the offer immediately and indefinitely. Scheduled offers appear/expire automatically.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sort order"><input type="number" value={editing.sort_order ?? 0} onChange={(e) => set("sort_order", Number(e.target.value))} data-testid="offer-sort" className={inputCls} /></Field>
              <label className="flex items-center gap-2 mt-6 font-semibold text-slate-700">Active
                <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => set("is_active", e.target.checked)} data-testid="offer-active" className="w-5 h-5 accent-red-600" /></label>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} data-testid="offer-save" className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl">Save offer</button>
              <button onClick={() => setEditing(null)} className="px-5 bg-slate-100 text-slate-600 font-semibold py-2.5 rounded-xl">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== CASHBACK & DISCOUNTS ====================
const REWARD_NETWORKS = ["MTN", "Airtel", "Glo", "9mobile"];
const CashbackSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    adminApi.get("/rewards").then((r) => {
      const d = r.data || {};
      setCfg({
        cashback_enabled: d.cashback_enabled !== false,
        airtime: { rate: d.airtime?.rate ?? 0, providers: d.airtime?.providers || {} },
        data: { rate: d.data?.rate ?? 0, providers: d.data?.providers || {} },
      });
    });
  }, []);
  if (!cfg) return <Spinner />;

  const setRate = (svc, val) => setCfg({ ...cfg, [svc]: { ...cfg[svc], rate: Number(val) || 0 } });
  const setOverride = (svc, net, val) => {
    const providers = { ...cfg[svc].providers };
    if (val === "" || val === null) delete providers[net];
    else providers[net] = Number(val) || 0;
    setCfg({ ...cfg, [svc]: { ...cfg[svc], providers } });
  };
  const save = async () => { await adminApi.put("/rewards", cfg); setMsg("Saved"); setTimeout(() => setMsg(""), 2000); };

  const serviceCard = (svc, label) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">{label}</h3>
        <Badge color="bg-red-100 text-red-700">{cfg[svc].rate}% bulk</Badge>
      </div>
      <Field label="Bulk discount / cashback rate (%) — applies to all networks">
        <input type="number" step="0.5" value={cfg[svc].rate} onChange={(e) => setRate(svc, e.target.value)} data-testid={`rewards-${svc}-rate`} className={inputCls} />
      </Field>
      <div>
        <p className="text-xs font-semibold text-slate-500 mb-2">Per-network overrides (leave blank to use bulk rate)</p>
        <div className="grid grid-cols-2 gap-3">
          {REWARD_NETWORKS.map((net) => (
            <Field key={net} label={`${net} (%)`}>
              <input type="number" step="0.5" value={cfg[svc].providers[net] ?? ""} placeholder={`${cfg[svc].rate}`} onChange={(e) => setOverride(svc, net, e.target.value)} data-testid={`rewards-${svc}-${net}`} className={inputCls} />
            </Field>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-rewards">
      <label className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 p-5">
        <span className="font-semibold text-slate-700">Cashback &amp; discounts enabled</span>
        <input type="checkbox" checked={cfg.cashback_enabled} onChange={(e) => setCfg({ ...cfg, cashback_enabled: e.target.checked })} data-testid="rewards-enabled" className="w-5 h-5 accent-red-600" />
      </label>
      {serviceCard("airtime", "Airtime")}
      {serviceCard("data", "Data")}
      <div>
        <button onClick={save} data-testid="rewards-save" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl">Save Cashback &amp; Discounts</button>
        {msg && <span className="ml-3 text-green-600 font-semibold">{msg}</span>}
      </div>
      <p className="text-sm text-slate-400">Discounts are credited to the customer's wallet bonus balance as cashback after a successful top-up. Per-network overrides take precedence over the bulk rate.</p>
    </div>
  );
};

const WHEEL_COLORS = ["#06B6D4", "#10B981", "#8B5CF6", "#F59E0B", "#E11D2E", "#374151", "#1F2A3C", "#EC4899", "#3B82F6"];

const _polar = (cx, cy, r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
const WheelPreview = ({ prizes }) => {
  const [rot, setRot] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const size = 280, cx = 140, cy = 140, r = 134;
  const total = prizes.reduce((s, p) => s + (Number(p.weight) || 0), 0) || 1;
  let acc = -Math.PI / 2;
  const slices = prizes.map((p) => {
    const frac = (Number(p.weight) || 0) / total;
    const a0 = acc, a1 = acc + frac * 2 * Math.PI; acc = a1;
    const [x0, y0] = _polar(cx, cy, r, a0), [x1, y1] = _polar(cx, cy, r, a1);
    const large = (a1 - a0) > Math.PI ? 1 : 0;
    const [lx, ly] = _polar(cx, cy, r * 0.6, (a0 + a1) / 2);
    const deg = ((a0 + a1) / 2) * 180 / Math.PI;
    return { p, d: `M${cx},${cy} L${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} Z`, lx, ly, frac, deg };
  });
  const spin = () => { if (spinning) return; setSpinning(true); setRot((v) => v + 1800 + Math.floor(Math.random() * 360)); setTimeout(() => setSpinning(false), 3200); };
  return (
    <div className="flex flex-col items-center" data-testid="wheel-preview">
      <div className="relative" style={{ width: size, height: size }}>
        <div style={{ position: "absolute", top: -4, left: "50%", transform: "translateX(-50%)", zIndex: 2, width: 0, height: 0, borderLeft: "10px solid transparent", borderRight: "10px solid transparent", borderTop: "18px solid #0f172a" }} />
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: `rotate(${rot}deg)`, transition: spinning ? "transform 3s cubic-bezier(0.17,0.67,0.12,0.99)" : "none" }}>
          {slices.map((s, i) => (
            <g key={i}>
              <path d={s.d} fill={s.p.color} stroke="#fff" strokeWidth="2" />
              {s.frac > 0.04 && (
                <text x={s.lx} y={s.ly} fill="#fff" fontSize="9" fontWeight="700" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${s.deg + 90} ${s.lx} ${s.ly})`}>{String(s.p.label || "").slice(0, 12)}</text>
              )}
            </g>
          ))}
          <circle cx={cx} cy={cy} r="22" fill="#fff" stroke="#e5e7eb" strokeWidth="2" />
        </svg>
      </div>
      <button onClick={spin} disabled={spinning} data-testid="wheel-preview-spin" className="mt-3 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
        <Icon name="casino" className="text-base" />{spinning ? "Spinning…" : "Spin preview"}
      </button>
      <p className="text-xs text-slate-400 mt-2 text-center">Slice size = win odds. Just a visual preview.</p>
    </div>
  );
};

const RewardsWheelAdmin = () => {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.get("/rewards-wheel").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return <Spinner />;

  const setPrize = (i, k, v) => setCfg((c) => ({ ...c, prizes: c.prizes.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const addPrize = () => setCfg((c) => ({ ...c, prizes: [...c.prizes, { id: `p${Date.now()}`, label: "New prize", value: 0, weight: 10, color: WHEEL_COLORS[c.prizes.length % WHEEL_COLORS.length] }] }));
  const removePrize = (i) => setCfg((c) => ({ ...c, prizes: c.prizes.filter((_, idx) => idx !== i) }));

  const totalWeight = cfg.prizes.reduce((s, p) => s + (Number(p.weight) || 0), 0) || 1;

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const payload = {
        enabled: cfg.enabled,
        spin_every: Math.max(1, Number(cfg.spin_every) || 5),
        prizes: cfg.prizes.map((p) => ({ id: p.id, label: p.label, value: Number(p.value) || 0, weight: Math.max(1, Number(p.weight) || 1), color: p.color })),
      };
      const r = await adminApi.put("/rewards-wheel", payload);
      setCfg(r.data); setMsg("Saved");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="admin-rewards-wheel">
      <div className="lg:col-span-2 space-y-5">
      <div className="flex flex-wrap items-center gap-4 bg-white rounded-2xl border border-slate-200 p-5">
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="wheel-enabled" className="w-5 h-5 accent-red-600" />
          <span className="font-semibold text-slate-700">Rewards Wheel enabled</span>
        </label>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-slate-500">1 spin per</span>
          <input type="number" min="1" value={cfg.spin_every} onChange={(e) => setCfg({ ...cfg, spin_every: e.target.value })} data-testid="wheel-spin-every" className="w-20 h-10 px-3 rounded-lg border border-slate-200 text-center" />
          <span className="text-sm text-slate-500">successful transactions</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900">Prize slices</h3>
          <button onClick={addPrize} data-testid="wheel-add-prize" className="text-sm font-semibold text-red-600 flex items-center gap-1"><Icon name="add" className="text-base" /> Add slice</button>
        </div>
        <div className="space-y-2">
          {cfg.prizes.map((p, i) => (
            <div key={p.id} className="flex items-center gap-2 p-2 rounded-xl border border-slate-100" data-testid={`wheel-prize-${i}`}>
              <input type="color" value={p.color} onChange={(e) => setPrize(i, "color", e.target.value)} className="w-9 h-9 rounded-lg border-0 bg-transparent cursor-pointer shrink-0" data-testid={`wheel-color-${i}`} />
              <input value={p.label} onChange={(e) => setPrize(i, "label", e.target.value)} placeholder="Label" data-testid={`wheel-label-${i}`} className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-xs text-slate-400">₦</span>
                <input type="number" value={p.value} onChange={(e) => setPrize(i, "value", e.target.value)} title="Cashback (0 = no win)" data-testid={`wheel-value-${i}`} className="w-20 h-10 px-2 rounded-lg border border-slate-200 text-sm text-center" />
              </div>
              <div className="flex flex-col items-center shrink-0">
                <input type="number" min="1" value={p.weight} onChange={(e) => setPrize(i, "weight", e.target.value)} title="Odds weight" data-testid={`wheel-weight-${i}`} className="w-16 h-10 px-2 rounded-lg border border-slate-200 text-sm text-center" />
                <span className="text-[10px] text-slate-400">{((Number(p.weight) || 0) / totalWeight * 100).toFixed(0)}%</span>
              </div>
              <button onClick={() => removePrize(i)} data-testid={`wheel-remove-${i}`} className="p-2 text-slate-300 hover:text-red-600 shrink-0"><Icon name="delete" className="text-lg" /></button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">Set cashback to ₦0 for "no win" slices. Odds weight controls how often a slice is hit (shown as %). Winning slices credit the customer's wallet bonus.</p>
      </div>

      <div>
        <button onClick={save} disabled={saving} data-testid="wheel-save" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-xl disabled:opacity-50">{saving ? "Saving…" : "Save Rewards Wheel"}</button>
        {msg && <span className="ml-3 text-green-600 font-semibold">{msg}</span>}
      </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-4 self-start bg-white rounded-2xl border border-slate-200 p-5">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3 text-center">Live preview</p>
        <WheelPreview prizes={cfg.prizes} />
      </div>
    </div>
  );
};

const RewardsPage = () => {
  const [tab, setTab] = useState("cashback");
  const tabs = [{ id: "cashback", label: "Cashback & Discounts", icon: "redeem" }, { id: "wheel", label: "Rewards Wheel", icon: "casino" }];
  return (
    <div data-testid="admin-rewards-page">
      <div className="flex gap-2 mb-5">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`rewards-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === t.id ? "bg-red-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            <Icon name={t.icon} className="text-base" />{t.label}
          </button>
        ))}
      </div>
      {tab === "cashback" ? <CashbackSettings /> : <RewardsWheelAdmin />}
    </div>
  );
};

// ==================== API MONITOR ====================
const ApiMonitorPage = () => {
  const [d, setD] = useState(null);
  const load = () => adminApi.get("/api-status").then((r) => setD(r.data));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  if (!d) return <Spinner />;
  return (
    <div data-testid="admin-api-monitor">
      <div className="flex items-center justify-between mb-4">
        <p className="text-slate-500 text-sm">Last checked {fmtDate(d.checked_at)}</p>
        <button onClick={load} data-testid="api-refresh" className="flex items-center gap-1 text-red-600 font-semibold text-sm"><Icon name="refresh" className="text-base" /> Refresh</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {d.services.map((s) => (
          <div key={s.name} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`service-${s.name}`}>
            <div className="flex items-center justify-between">
              <p className="font-bold text-slate-900">{s.name}</p>
              <Badge color={statusColor(s.status)}>{s.status.replace(/_/g, " ")}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-2">{s.detail}</p>
            {s.latency_ms != null && <p className="text-xs text-slate-400 mt-1">Latency: {s.latency_ms} ms</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== SECURITY ====================
const SecurityPage = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => { adminApi.get("/security").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner />;

  const unlock = async (raw) => { await adminApi.post("/security/unlock", { raw_identifier: raw }); load(); };
  const revoke = async (id) => { await adminApi.post("/security/revoke-session", { id }); load(); };
  const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
  const kindColor = { ip: "blue", admin: "red", user: "gray" };

  return (
    <div className="space-y-6" data-testid="admin-security">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="security-active-lockouts">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Active Lockouts</p>
          <p className="text-3xl font-bold text-red-600 mt-1">{d.summary.active_lockouts}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Tracked Identifiers</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{d.summary.tracked_identifiers}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="security-active-sessions">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Active Sessions</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{d.summary.active_sessions}</p>
        </div>
      </div>

      {/* Lockouts / failed logins */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">Failed Logins & Lockouts</h3>
          <button onClick={load} data-testid="security-refresh" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"><Icon name="refresh" className="text-base" /> Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-5 py-3">Identifier</th><th className="text-left px-3 py-3">Type</th><th className="text-left px-3 py-3">Fails</th><th className="text-left px-3 py-3">Status</th><th className="text-left px-3 py-3">Last attempt</th><th className="text-right px-5 py-3">Action</th></tr>
            </thead>
            <tbody>
              {d.lockouts.length === 0 && <tr><td colSpan="6" className="px-5 py-6 text-center text-slate-400">No failed-login activity.</td></tr>}
              {d.lockouts.map((l, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`lockout-row-${i}`}>
                  <td className="px-5 py-3 font-medium text-slate-800 break-all">{l.identifier}</td>
                  <td className="px-3 py-3"><Badge color={kindColor[l.kind] || "bg-slate-100 text-slate-600"}>{l.kind}</Badge></td>
                  <td className="px-3 py-3">{l.failed_count}</td>
                  <td className="px-3 py-3">{l.locked ? <Badge color="bg-red-100 text-red-700">Locked</Badge> : <Badge color="bg-green-100 text-green-700">Active</Badge>}</td>
                  <td className="px-3 py-3 text-slate-500">{fmt(l.updated_at)}</td>
                  <td className="px-5 py-3 text-right">
                    {l.locked && <button onClick={() => unlock(l.raw_identifier)} data-testid={`unlock-btn-${i}`} className="text-xs font-semibold bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg">Unlock</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active sessions */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-bold text-slate-900">Active Sessions</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-5 py-3">Account</th><th className="text-left px-3 py-3">Type</th><th className="text-left px-3 py-3">IP</th><th className="text-left px-3 py-3">Device</th><th className="text-left px-3 py-3">Signed in</th><th className="text-right px-5 py-3">Action</th></tr>
            </thead>
            <tbody>
              {d.active_sessions.length === 0 && <tr><td colSpan="6" className="px-5 py-6 text-center text-slate-400">No active sessions.</td></tr>}
              {d.active_sessions.map((s, i) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`session-row-${i}`}>
                  <td className="px-5 py-3 font-medium text-slate-800 break-all">{s.email}</td>
                  <td className="px-3 py-3"><Badge color={s.type === "admin" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}>{s.type}</Badge></td>
                  <td className="px-3 py-3 text-slate-500">{s.ip}</td>
                  <td className="px-3 py-3 text-slate-500 max-w-[220px] truncate" title={s.user_agent}>{s.user_agent || "—"}</td>
                  <td className="px-3 py-3 text-slate-500">{fmt(s.created_at)}</td>
                  <td className="px-5 py-3 text-right"><button onClick={() => revoke(s.id)} data-testid={`revoke-btn-${i}`} className="text-xs font-semibold bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg">Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-5 py-3 text-xs text-slate-400 border-t border-slate-100">Sessions are login records (JWT is stateless). "Remove" clears the record from this list.</p>
      </div>
    </div>
  );
};

const AUDIT_META = {
  balance_adjustment: { label: "Balance Adjustment", color: "bg-red-100 text-red-700", icon: "account_balance_wallet" },
  order_refund: { label: "Refund", color: "bg-amber-100 text-amber-700", icon: "undo" },
  customer_status: { label: "Customer Status", color: "bg-blue-100 text-blue-700", icon: "manage_accounts" },
  order_retry: { label: "Order Retry", color: "bg-slate-100 text-slate-600", icon: "refresh" },
  security_unlock: { label: "Unlock", color: "bg-green-100 text-green-700", icon: "lock_open" },
  session_revoke: { label: "Session Revoke", color: "bg-slate-100 text-slate-600", icon: "logout" },
};

const auditDetail = (row) => {
  const d = row.details || {};
  switch (row.action) {
    case "balance_adjustment":
      return `${d.type} ${naira(d.amount)} (${d.target}) — ${d.reason || ""} · ${naira(d.balance_before)} → ${naira(d.balance_after)}`;
    case "order_refund":
      return `${naira(d.amount)} refunded to wallet (bal ${naira(d.balance_after)})`;
    case "customer_status":
      return `Set to ${d.status || (d.is_active ? "active" : "suspended")}`;
    case "order_retry":
      return `${d.status_code || ""} — ${d.response_message || ""}`;
    default:
      return Object.keys(d).length ? JSON.stringify(d) : "—";
  }
};

const AuditLogPage = () => {
  const [data, setData] = useState(null);
  const [action, setAction] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setData(null);
    adminApi.get(`/audit-logs?action=${action}&search=${encodeURIComponent(search)}&page=${page}&limit=20`)
      .then((r) => setData(r.data)).catch(() => {});
  }, [action, search, page]);
  useEffect(() => { load(); }, [load]);

  const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-5" data-testid="admin-audit-log">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input data-testid="audit-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search admin, target, reference..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500" />
        </div>
        <select value={action} onChange={(e) => { setPage(1); setAction(e.target.value); }} data-testid="audit-action-filter" className="border border-slate-200 rounded-xl px-4 py-2.5 outline-none">
          <option value="all">All actions</option>
          {(data?.actions || Object.keys(AUDIT_META)).map((a) => (
            <option key={a} value={a}>{AUDIT_META[a]?.label || a}</option>
          ))}
        </select>
        <button onClick={load} data-testid="audit-refresh" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 px-3 border border-slate-200 rounded-xl"><Icon name="refresh" className="text-base" /> Refresh</button>
      </div>

      {!data ? <Spinner /> : (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-5 py-3">When</th>
                    <th className="text-left px-3 py-3">Admin</th>
                    <th className="text-left px-3 py-3">Action</th>
                    <th className="text-left px-3 py-3">Target</th>
                    <th className="text-left px-3 py-3">Details</th>
                    <th className="text-left px-5 py-3">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && <tr><td colSpan="6" className="px-5 py-8 text-center text-slate-400">No admin actions logged yet.</td></tr>}
                  {data.items.map((row, i) => {
                    const meta = AUDIT_META[row.action] || { label: row.action, color: "bg-slate-100 text-slate-600", icon: "history" };
                    return (
                      <tr key={row.id} className="border-t border-slate-100 align-top" data-testid={`audit-row-${i}`}>
                        <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmt(row.created_at)}</td>
                        <td className="px-3 py-3 font-medium text-slate-800 break-all">{row.admin_email}</td>
                        <td className="px-3 py-3"><Badge color={meta.color}><span className="inline-flex items-center gap-1"><Icon name={meta.icon} className="text-sm" />{meta.label}</span></Badge></td>
                        <td className="px-3 py-3 text-slate-700 break-all">{row.target_label || row.target_id}</td>
                        <td className="px-3 py-3 text-slate-500 max-w-[340px]">{auditDetail(row)}</td>
                        <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{row.ip_address || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="px-5 py-3 text-xs text-slate-400 border-t border-slate-100">{data.total} total action{data.total === 1 ? "" : "s"} logged. Audit entries are immutable.</p>
          </div>
          <Pagination page={data.page} pages={data.pages} onPage={setPage} />
        </>
      )}
    </div>
  );
};


// ==================== LAYOUT ====================
const PARTNER_STATUS_COLOR = { pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", suspended: "bg-red-100 text-red-700" };
const COMM_STATUS_COLOR = { pending: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700", paid: "bg-green-100 text-green-700", reversed: "bg-slate-100 text-slate-500" };

const PartnerEditModal = ({ partner, onClose, onSaved }) => {
  const c = partner.commission || {};
  const [name, setName] = useState(partner.name || "");
  const [company, setCompany] = useState(partner.company || "");
  const [airType, setAirType] = useState(c.airtime?.type || "percent");
  const [airRate, setAirRate] = useState(c.airtime?.rate ?? "");
  const [dataType, setDataType] = useState(c.data?.type || "fixed");
  const [dataRate, setDataRate] = useState(c.data?.rate ?? "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");

  const buildCommission = () => {
    const comm = {};
    if (airRate !== "" && Number(airRate) > 0) comm.airtime = { type: airType, rate: Number(airRate) };
    if (dataRate !== "" && Number(dataRate) > 0) comm.data = { type: dataType, rate: Number(dataRate) };
    return comm;
  };

  const save = async (status) => {
    setBusy(status || "save"); setMsg("");
    try {
      const body = { name, company, commission: buildCommission() };
      if (status) body.status = status;
      await adminApi.patch(`/partners/${partner.id}`, body);
      onSaved();
      if (!status) setMsg("Saved");
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  const payout = async () => {
    setBusy("payout"); setMsg("");
    try { const r = await adminApi.post(`/partners/${partner.id}/payout`, {}); setMsg(`Paid out ${naira(r.data.amount)} (${r.data.count} commissions)`); onSaved(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Payout failed"); } finally { setBusy(""); }
  };

  const del = async () => {
    if (!window.confirm(`Delete partner ${partner.email}? This removes their code, commissions and payouts, and unlinks their referred customers. This cannot be undone.`)) return;
    setBusy("delete"); setMsg("");
    try { await adminApi.delete(`/partners/${partner.id}`); onSaved(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Delete failed"); setBusy(""); }
  };

  const e = partner.earnings || {};
  return (
    <Modal title={`Partner — ${partner.email}`} onClose={onClose} testid="partner-edit-modal">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {["pending", "approved", "paid", "reversed"].map((k) => (
          <div key={k} className="bg-slate-50 rounded-xl p-3"><p className="text-xs uppercase text-slate-400">{k}</p><p className="font-bold text-slate-900">{naira(e[k])}</p></div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name"><input value={name} onChange={(ev) => setName(ev.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-lg outline-none" data-testid="partner-edit-name" /></Field>
        <Field label="Company"><input value={company} onChange={(ev) => setCompany(ev.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-lg outline-none" /></Field>
      </div>

      {(() => {
        const hasKyc = partner.business_name || partner.business_reg_no || partner.business_tin || partner.business_type || partner.director_name || partner.director_nin || partner.gov_id_number || partner.business_address;
        return (
        <div className="mt-5" data-testid="partner-business-details">
          <h4 className="font-bold text-slate-900 mb-2">Business application details</h4>
          {!hasKyc && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-testid="partner-legacy-note">
              This applicant signed up before the application form was added, so business details were not collected.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 bg-slate-50 rounded-xl p-4 text-sm">
            {[
              ["Business name", partner.business_name],
              ["Business type", partner.business_type],
              ["Registration number", partner.business_reg_no],
              ["Business TIN", partner.business_tin],
              ["Director's name", partner.director_name],
              ["Director's NIN", partner.director_nin],
              ["Gov-issued ID number", partner.gov_id_number],
              ["Phone", partner.phone],
              ["Business address", partner.business_address],
            ].map(([label, val]) => (
              <div key={label} className={label === "Business address" ? "sm:col-span-2" : ""}>
                <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
                <p className="font-semibold text-slate-800 break-words">{val || "Not provided"}</p>
              </div>
            ))}
          </div>
        </div>
        );
      })()}

      <h4 className="font-bold text-slate-900 mt-5 mb-2">Commission rates</h4>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="w-20 text-sm font-semibold text-slate-600">Airtime</span>
          <select value={airType} onChange={(ev) => setAirType(ev.target.value)} data-testid="partner-air-type" className="h-10 px-2 border border-slate-200 rounded-lg outline-none"><option value="percent">% of sale</option><option value="fixed">Fixed ₦</option></select>
          <input type="number" value={airRate} onChange={(ev) => setAirRate(ev.target.value)} placeholder="rate" data-testid="partner-air-rate" className="h-10 px-3 border border-slate-200 rounded-lg outline-none w-32" />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-20 text-sm font-semibold text-slate-600">Data</span>
          <select value={dataType} onChange={(ev) => setDataType(ev.target.value)} data-testid="partner-data-type" className="h-10 px-2 border border-slate-200 rounded-lg outline-none"><option value="percent">% of sale</option><option value="fixed">Fixed ₦</option></select>
          <input type="number" value={dataRate} onChange={(ev) => setDataRate(ev.target.value)} placeholder="rate" data-testid="partner-data-rate" className="h-10 px-3 border border-slate-200 rounded-lg outline-none w-32" />
        </div>
      </div>

      {msg && <p className="text-sm mt-3 text-slate-600" data-testid="partner-edit-msg">{msg}</p>}

      <div className="flex flex-wrap gap-2 mt-6">
        <button onClick={() => save(null)} disabled={!!busy} data-testid="partner-save" className="px-4 py-2 bg-slate-900 text-white rounded-lg font-semibold disabled:opacity-50">Save</button>
        {partner.status !== "approved" && <button onClick={() => save("approved")} disabled={!!busy} data-testid="partner-approve" className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold disabled:opacity-50">Approve</button>}
        {partner.status !== "suspended" && <button onClick={() => save("suspended")} disabled={!!busy} data-testid="partner-suspend" className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold disabled:opacity-50">Suspend</button>}
        {partner.status === "suspended" && <button onClick={() => save("approved")} disabled={!!busy} className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold disabled:opacity-50">Reactivate</button>}
        <button onClick={payout} disabled={!!busy || (e.approved || 0) <= 0} data-testid="partner-payout" className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold disabled:opacity-40 ml-auto">Pay out {naira(e.approved)}</button>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <button onClick={del} disabled={!!busy} data-testid="partner-delete" className="text-red-600 hover:text-red-700 text-sm font-semibold flex items-center gap-1 disabled:opacity-50"><Icon name="delete" className="text-base" /> Delete partner</button>
      </div>
    </Modal>
  );
};

const PartnersPage = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newP, setNewP] = useState({ name: "", email: "", company: "" });
  const [createMsg, setCreateMsg] = useState("");

  const load = useCallback(() => {
    setData(null);
    adminApi.get(`/partners?status=${status}&search=${encodeURIComponent(search)}&page=${page}&limit=12`).then((r) => setData(r.data)).catch(() => {});
  }, [status, search, page]);
  useEffect(() => { load(); }, [load]);

  const createPartner = async () => {
    setCreateMsg("");
    try { const r = await adminApi.post("/partners", newP); setCreateMsg(`Created. Code: ${r.data.code}${r.data.temp_password ? `, temp password: ${r.data.temp_password}` : ""}`); setNewP({ name: "", email: "", company: "" }); load(); }
    catch (e) { setCreateMsg(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-partners">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input data-testid="partners-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search name, email, company..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500" />
        </div>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="partners-status-filter" className="border border-slate-200 rounded-xl px-4 py-2.5 outline-none">
          <option value="all">All status</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="suspended">Suspended</option>
        </select>
        <button onClick={() => setCreating(!creating)} data-testid="partners-create-toggle" className="px-4 py-2.5 bg-slate-900 text-white rounded-xl font-semibold flex items-center gap-1"><Icon name="add" className="text-base" /> Partner</button>
      </div>

      {creating && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-end gap-3" data-testid="partners-create-form">
          <Field label="Name"><input value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })} className="h-10 px-3 border border-slate-200 rounded-lg outline-none" data-testid="new-partner-name" /></Field>
          <Field label="Email"><input value={newP.email} onChange={(e) => setNewP({ ...newP, email: e.target.value })} className="h-10 px-3 border border-slate-200 rounded-lg outline-none" data-testid="new-partner-email" /></Field>
          <Field label="Company"><input value={newP.company} onChange={(e) => setNewP({ ...newP, company: e.target.value })} className="h-10 px-3 border border-slate-200 rounded-lg outline-none" /></Field>
          <button onClick={createPartner} data-testid="new-partner-submit" className="h-10 px-4 bg-red-600 text-white rounded-lg font-semibold">Create</button>
          {createMsg && <span className="text-sm text-slate-600 w-full">{createMsg}</span>}
        </div>
      )}

      {!data ? <Spinner /> : (
        <>
          <div className="grid gap-3">
            {data.items.length === 0 && <div className="text-center text-slate-400 py-10">No partners found.</div>}
            {data.items.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`partner-card-${p.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-slate-900">{p.company || p.name}</p>
                    <Badge color={PARTNER_STATUS_COLOR[p.status] || "bg-slate-100"}>{p.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-500">{p.email} · {(p.codes || []).map((c) => c.code).join(", ")}</p>
                  <p className="text-xs text-slate-400 mt-1">Approved {naira(p.earnings?.approved)} · Paid {naira(p.earnings?.paid)} · Pending {naira(p.earnings?.pending)}</p>
                </div>
                <button onClick={() => setEditing(p)} data-testid={`partner-manage-${p.id}`} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-50 self-start md:self-center">Manage</button>
              </div>
            ))}
          </div>
          <Pagination page={data.page} pages={data.pages} onPage={setPage} />
        </>
      )}

      {editing && <PartnerEditModal partner={editing} onClose={() => setEditing(null)} onSaved={() => { load(); setEditing(null); }} />}
    </div>
  );
};

const CommissionsPage = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setData(null);
    adminApi.get(`/commissions?status=${status}&page=${page}&limit=25`).then((r) => setData(r.data)).catch(() => {});
  }, [status, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-commissions">
      <div className="flex items-center justify-between gap-3">
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="commissions-status-filter" className="border border-slate-200 rounded-xl px-4 py-2.5 outline-none">
          <option value="all">All</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="paid">Paid</option><option value="reversed">Reversed</option>
        </select>
        {data && <div className="text-sm text-slate-600"><span className="font-bold">{data.totals.count}</span> commissions · <span className="font-bold">{naira(data.totals.amount)}</span></div>}
      </div>
      {!data ? <Spinner /> : (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr><th className="text-left px-5 py-3">Date</th><th className="text-left px-3 py-3">Code</th><th className="text-left px-3 py-3">Service</th><th className="text-left px-3 py-3">Sale</th><th className="text-left px-3 py-3">Commission</th><th className="text-left px-5 py-3">Status</th></tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && <tr><td colSpan="6" className="px-5 py-8 text-center text-slate-400">No commissions.</td></tr>}
                  {data.items.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100">
                      <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-3 font-mono text-slate-700">{c.code}</td>
                      <td className="px-3 py-3 capitalize text-slate-700">{c.service_type}</td>
                      <td className="px-3 py-3 text-slate-500">{naira(c.sale_amount)}</td>
                      <td className="px-3 py-3 font-bold text-slate-900">{naira(c.commission_amount)}</td>
                      <td className="px-5 py-3"><Badge color={COMM_STATUS_COLOR[c.effective_status] || "bg-slate-100"}>{c.effective_status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination page={data.page} pages={data.pages} onPage={setPage} />
        </>
      )}
    </div>
  );
};


const ENT_STATUS_COLOR = { active: "bg-green-100 text-green-700", suspended: "bg-red-100 text-red-700" };

const EnterprisesPage = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newE, setNewE] = useState({ name: "", owner_email: "", owner_name: "", company: "", discount: { airtime_pct: 0, data_pct: 0 }, initial_credit: 0 });
  const [createMsg, setCreateMsg] = useState("");

  const load = useCallback(() => {
    setData(null);
    adminApi.get(`/enterprises?status=${status}&search=${encodeURIComponent(search)}&page=${page}&limit=12`).then((r) => setData(r.data)).catch(() => {});
  }, [status, search, page]);
  useEffect(() => { load(); }, [load]);

  const createEnterprise = async () => {
    setCreateMsg("");
    try {
      const r = await adminApi.post("/enterprises", newE);
      setCreateMsg(`Created. Owner: ${r.data.owner_email}${r.data.temp_password ? ` · temp password: ${r.data.temp_password}` : ""}`);
      setNewE({ name: "", owner_email: "", owner_name: "", company: "", discount: { airtime_pct: 0, data_pct: 0 }, initial_credit: 0 });
      load();
    } catch (e) { setCreateMsg(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-enterprises">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input data-testid="enterprises-search" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search org, owner email, company..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500" />
        </div>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="enterprises-status-filter" className="border border-slate-200 rounded-xl px-4 py-2.5 outline-none">
          <option value="all">All status</option><option value="active">Active</option><option value="suspended">Suspended</option>
        </select>
        <button onClick={() => setCreating(!creating)} data-testid="enterprises-create-toggle" className="px-4 py-2.5 bg-slate-900 text-white rounded-xl font-semibold flex items-center gap-1"><Icon name="add" className="text-base" /> Enterprise</button>
      </div>

      {creating && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end" data-testid="enterprises-create-form">
          <Field label="Organization name"><input value={newE.name} onChange={(e) => setNewE({ ...newE, name: e.target.value })} className={inputCls} data-testid="new-ent-name" /></Field>
          <Field label="Company (optional)"><input value={newE.company} onChange={(e) => setNewE({ ...newE, company: e.target.value })} className={inputCls} /></Field>
          <Field label="Owner name"><input value={newE.owner_name} onChange={(e) => setNewE({ ...newE, owner_name: e.target.value })} className={inputCls} data-testid="new-ent-owner-name" /></Field>
          <Field label="Owner email (login)"><input value={newE.owner_email} onChange={(e) => setNewE({ ...newE, owner_email: e.target.value })} className={inputCls} data-testid="new-ent-owner-email" /></Field>
          <Field label="Owner password (optional)"><input value={newE.owner_password || ""} onChange={(e) => setNewE({ ...newE, owner_password: e.target.value })} placeholder="auto-generated if blank" className={inputCls} /></Field>
          <Field label="Initial wallet credit (₦)"><input type="number" value={newE.initial_credit} onChange={(e) => setNewE({ ...newE, initial_credit: Number(e.target.value) })} className={inputCls} /></Field>
          <Field label="Airtime discount %"><input type="number" value={newE.discount.airtime_pct} onChange={(e) => setNewE({ ...newE, discount: { ...newE.discount, airtime_pct: Number(e.target.value) } })} className={inputCls} data-testid="new-ent-airtime-pct" /></Field>
          <Field label="Data discount %"><input type="number" value={newE.discount.data_pct} onChange={(e) => setNewE({ ...newE, discount: { ...newE.discount, data_pct: Number(e.target.value) } })} className={inputCls} data-testid="new-ent-data-pct" /></Field>
          <button onClick={createEnterprise} data-testid="new-ent-submit" className="h-11 px-4 bg-red-600 text-white rounded-xl font-semibold">Create Enterprise</button>
          {createMsg && <span className="text-sm text-slate-600 lg:col-span-3" data-testid="new-ent-msg">{createMsg}</span>}
        </div>
      )}

      {!data ? <Spinner /> : (
        <>
          <div className="grid gap-3">
            {data.items.length === 0 && <div className="text-center text-slate-400 py-10">No enterprises found.</div>}
            {data.items.map((e) => (
              <div key={e.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`enterprise-card-${e.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-slate-900">{e.name}</p>
                    <Badge color={ENT_STATUS_COLOR[e.status] || "bg-slate-100"}>{e.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-500">{e.owner_email}{e.company ? ` · ${e.company}` : ""}</p>
                  <p className="text-xs text-slate-400 mt-1">Wallet {naira(e.wallet_balance)} · Airtime −{e.discount?.airtime_pct || 0}% · Data −{e.discount?.data_pct || 0}% · {e.member_count} members · {e.topup_count} top-ups</p>
                </div>
                <button onClick={() => setEditing(e)} data-testid={`enterprise-manage-${e.id}`} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-50 self-start md:self-center">Manage</button>
              </div>
            ))}
          </div>
          <Pagination page={data.page} pages={data.pages} onPage={setPage} />
        </>
      )}

      {editing && <EnterpriseEditModal enterprise={editing} onClose={() => setEditing(null)} onSaved={() => { load(); setEditing(null); }} />}
    </div>
  );
};

const EnterpriseEditModal = ({ enterprise, onClose, onSaved }) => {
  const [e, setE] = useState(enterprise);
  const [airtime, setAirtime] = useState(enterprise.discount?.airtime_pct || 0);
  const [dataPct, setDataPct] = useState(enterprise.discount?.data_pct || 0);
  const [creditAmt, setCreditAmt] = useState(0);
  const [creditType, setCreditType] = useState("credit");
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");

  const refresh = useCallback(() => {
    adminApi.get(`/enterprises/${enterprise.id}`).then((r) => { setE(r.data); setAirtime(r.data.discount?.airtime_pct || 0); setDataPct(r.data.discount?.data_pct || 0); }).catch(() => {});
  }, [enterprise.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const saveDiscounts = async () => {
    setMsg("");
    try { await adminApi.patch(`/enterprises/${e.id}`, { discount: { airtime_pct: Number(airtime), data_pct: Number(dataPct) } }); setMsg("Discounts updated"); onSaved(); }
    catch (err) { setMsg(err?.response?.data?.detail || "Failed"); }
  };
  const toggleStatus = async () => {
    const next = e.status === "active" ? "suspended" : "active";
    await adminApi.patch(`/enterprises/${e.id}`, { status: next }); onSaved();
  };
  const applyCredit = async () => {
    setMsg("");
    if (!creditAmt || creditAmt <= 0) return setMsg("Enter an amount");
    try { const r = await adminApi.post(`/enterprises/${e.id}/credit`, { amount: Number(creditAmt), type: creditType, reason }); setMsg(`New balance ${naira(r.data.wallet_balance)}`); setCreditAmt(0); setReason(""); refresh(); onSaved(); }
    catch (err) { setMsg(err?.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${e.name}? This removes its team, departments, members and history. This cannot be undone.`)) return;
    await adminApi.delete(`/enterprises/${e.id}`); onSaved();
  };

  return (
    <Modal title={`Manage · ${e.name}`} onClose={onClose} testid="enterprise-edit-modal">
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-500">Wallet</p><p className="font-bold text-slate-900" data-testid="ent-modal-balance">{naira(e.wallet_balance)}</p></div>
          <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-500">Members</p><p className="font-bold text-slate-900">{e.member_count ?? "—"}</p></div>
          <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-500">Top-ups</p><p className="font-bold text-slate-900">{e.topup_count ?? "—"}</p></div>
          <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-500">Spent</p><p className="font-bold text-slate-900">{naira(e.total_spent)}</p></div>
        </div>

        <div>
          <p className="font-semibold text-slate-800 mb-2">Discounts (reduce wallet cost)</p>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Airtime %"><input type="number" value={airtime} onChange={(ev) => setAirtime(ev.target.value)} className={inputCls + " w-28"} data-testid="ent-edit-airtime-pct" /></Field>
            <Field label="Data %"><input type="number" value={dataPct} onChange={(ev) => setDataPct(ev.target.value)} className={inputCls + " w-28"} data-testid="ent-edit-data-pct" /></Field>
            <button onClick={saveDiscounts} data-testid="ent-edit-save-discounts" className="h-11 px-4 bg-red-600 text-white rounded-xl font-semibold">Save discounts</button>
          </div>
        </div>

        <div>
          <p className="font-semibold text-slate-800 mb-2">Adjust wallet</p>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Type"><select value={creditType} onChange={(ev) => setCreditType(ev.target.value)} className={inputCls + " w-32"} data-testid="ent-credit-type"><option value="credit">Credit</option><option value="debit">Debit</option></select></Field>
            <Field label="Amount (₦)"><input type="number" value={creditAmt} onChange={(ev) => setCreditAmt(ev.target.value)} className={inputCls + " w-32"} data-testid="ent-credit-amount" /></Field>
            <Field label="Reason"><input value={reason} onChange={(ev) => setReason(ev.target.value)} className={inputCls + " w-48"} data-testid="ent-credit-reason" /></Field>
            <button onClick={applyCredit} data-testid="ent-credit-apply" className="h-11 px-4 bg-slate-900 text-white rounded-xl font-semibold">Apply</button>
          </div>
        </div>

        {e.team && (
          <div>
            <p className="font-semibold text-slate-800 mb-2">Team</p>
            <div className="space-y-1">
              {e.team.map((u) => (
                <div key={u.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
                  <span className="text-slate-700">{u.name} · {u.email}</span>
                  <Badge color="bg-slate-200 text-slate-700">{u.role}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {e.schedules && (
          <div>
            <p className="font-semibold text-slate-800 mb-2">Schedules ({e.schedules.length})</p>
            {e.schedules.length === 0 ? (
              <p className="text-sm text-slate-400 bg-slate-50 rounded-lg px-3 py-2" data-testid="ent-modal-no-schedules">No scheduled top-ups.</p>
            ) : (
              <div className="space-y-1" data-testid="ent-modal-schedules">
                {e.schedules.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-slate-700 font-medium">{s.name}</p>
                      <p className="text-xs text-slate-400 capitalize">{s.frequency} · {naira(s.amount)} {s.service_type} · {(s.member_ids || []).length} member(s) · next {s.next_execution ? new Date(s.next_execution).toLocaleDateString() : "—"}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge color={s.schedule_type === "auto" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>{s.schedule_type === "auto" ? "Auto" : "Reminder"}</Badge>
                      <Badge color={s.is_active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}>{s.is_active ? "Active" : "Paused"}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {msg && <p className="text-sm text-slate-600" data-testid="ent-edit-msg">{msg}</p>}

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <button onClick={toggleStatus} data-testid="ent-toggle-status" className={`px-4 py-2.5 rounded-xl font-semibold ${e.status === "active" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
            {e.status === "active" ? "Suspend" : "Reactivate"}
          </button>
          <button onClick={del} data-testid="ent-delete" className="px-4 py-2.5 rounded-xl font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100">Delete enterprise</button>
        </div>
      </div>
    </Modal>
  );
};


// ==================== FRAUD & RISK (Phase E) ====================
const SEV_COLOR = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-600" };
const RISK_BAR = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-slate-400" };

const FraudDetailModal = ({ id, onClose, onActioned }) => {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminApi.get(`/fraud/${id}`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
  const act = async (action) => {
    if (action === "suspend" && !window.confirm("Suspend this customer's account?")) return;
    setBusy(true);
    try { await adminApi.post(`/fraud/${id}/action`, { action, note }); onActioned(); onClose(); }
    catch (e) { alert(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  if (!d) return <Modal title="Risk detail" onClose={onClose} testid="fraud-detail"><Spinner /></Modal>;
  const f = d.flag;
  return (
    <Modal title="Risk detail" onClose={onClose} testid="fraud-detail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-bold text-slate-900">{f.user_name || "—"}</p>
          <p className="text-sm text-slate-500">{f.user_email} · {f.user_phone}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-extrabold text-slate-900" data-testid="fraud-detail-score">{f.risk_score}</div>
          <Badge color={SEV_COLOR[f.severity]}>{f.severity} risk</Badge>
        </div>
      </div>

      {f.ai_explanation && (
        <div className="rounded-xl bg-violet-50 border border-violet-200 p-4 mb-4" data-testid="fraud-ai">
          <p className="text-xs font-bold text-violet-700 uppercase tracking-wide mb-1 flex items-center gap-1"><Icon name="auto_awesome" className="text-sm" />AI assessment</p>
          <p className="text-sm text-slate-700 whitespace-pre-line">{f.ai_explanation}</p>
        </div>
      )}

      <p className="text-xs font-bold text-slate-500 uppercase mb-2">Why it was flagged</p>
      <ul className="space-y-1.5 mb-4">
        {(f.reasons || []).map((r, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Icon name="flag" className="text-red-500 text-base mt-0.5" />{r}</li>
        ))}
      </ul>

      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        {[["24h txns", f.signals?.velocity_24h], ["Recipients/24h", f.signals?.distinct_recipients_24h], ["1h burst", f.signals?.max_burst_1h], ["Max amount", naira(f.signals?.max_amount_7d || 0)], ["30d avg", naira(f.signals?.avg_amount_30d || 0)], ["Failed", `${Math.round((f.signals?.failed_ratio || 0) * 100)}%`]].map(([l, v], i) => (
          <div key={i} className="bg-slate-50 rounded-xl py-2"><p className="text-[10px] text-slate-400">{l}</p><p className="font-bold text-slate-800 text-sm">{v}</p></div>
        ))}
      </div>

      <p className="text-xs font-bold text-slate-500 uppercase mb-2">Recent transactions</p>
      <div className="max-h-40 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100 mb-4">
        {(d.recent_transactions || []).map((t, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
            <span className="text-slate-600">{t.provider} {t.service_type} → {t.customer_phone}</span>
            <span className="font-semibold text-slate-800">{naira(t.amount)}</span>
          </div>
        ))}
      </div>

      {f.status === "open" ? (
        <>
          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="fraud-note" placeholder="Resolution note (optional)" className={inputCls + " mb-3"} />
          <div className="flex gap-2">
            <button onClick={() => act("dismiss")} disabled={busy} data-testid="fraud-dismiss" className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl">Dismiss</button>
            <button onClick={() => act("review")} disabled={busy} data-testid="fraud-review" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl">Mark reviewed</button>
            <button onClick={() => act("suspend")} disabled={busy} data-testid="fraud-suspend" className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl">Suspend user</button>
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3" data-testid="fraud-resolved">
          {f.status === "dismissed" ? "Dismissed" : "Reviewed"} by {f.resolved_by || "admin"}{f.resolution_note ? ` — "${f.resolution_note}"` : ""}{f.user_active === false ? " · account suspended" : ""}
        </div>
      )}
    </Modal>
  );
};

const FraudPage = () => {
  const [sum, setSum] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [status, setStatus] = useState("open");
  const [severity, setSeverity] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSummary = () => adminApi.get("/fraud/summary").then((r) => setSum(r.data)).catch(() => {});
  const load = useCallback(() => {
    setLoading(true);
    adminApi.get(`/fraud?status=${status}&severity=${severity}&search=${encodeURIComponent(search)}&page=${page}&limit=15`)
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [status, severity, search, page]);

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try { await adminApi.post("/fraud/scan"); loadSummary(); load(); }
    catch (e) { /* noop */ } finally { setScanning(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-fraud">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Fraud & Risk</h2>
          <p className="text-sm text-slate-400">AI-scored risk signals across customer activity. {sum?.last_scan ? `Last scan ${new Date(sum.last_scan).toLocaleString()}` : "Not scanned yet"}.</p>
        </div>
        <button onClick={runScan} disabled={scanning} data-testid="fraud-scan-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2.5 rounded-xl flex items-center gap-1.5 disabled:opacity-60">
          <Icon name={scanning ? "hourglass_top" : "radar"} />{scanning ? "Scanning…" : "Run scan now"}</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="flag" label="Open flags" value={sum?.open ?? "—"} accent="text-red-600" />
        <StatCard icon="priority_high" label="High severity" value={sum?.high ?? "—"} accent="text-red-600" />
        <StatCard icon="warning" label="Medium" value={sum?.medium ?? "—"} accent="text-amber-600" />
        <StatCard icon="check_circle" label="Reviewed" value={sum?.reviewed ?? "—"} accent="text-green-600" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="fraud-status-filter" className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm">
          {["open", "reviewed", "dismissed", "all"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(1); }} data-testid="fraud-severity-filter" className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm">
          {["all", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s === "all" ? "All severity" : s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="fraud-search" placeholder="Search email / name / phone" className="flex-1 min-w-[180px] bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm" />
      </div>

      {loading ? <Spinner /> : data.items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400" data-testid="fraud-empty">
          <Icon name="verified_user" className="text-5xl text-slate-300" />
          <p className="mt-2">No flags for this filter. Your customers look clean. 🎉</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((f) => (
            <button key={f.id} onClick={() => setDetailId(f.id)} data-testid={`fraud-row-${f.id}`}
              className="w-full text-left bg-white rounded-2xl border border-slate-200 p-4 hover:border-red-300 transition-colors flex items-center gap-4">
              <div className="w-14 text-center">
                <div className="text-2xl font-extrabold text-slate-900">{f.risk_score}</div>
                <div className="h-1.5 rounded-full bg-slate-100 mt-1"><div className={`h-1.5 rounded-full ${RISK_BAR[f.severity]}`} style={{ width: `${f.risk_score}%` }} /></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-slate-900 truncate">{f.user_name || f.user_email}</p>
                  <Badge color={SEV_COLOR[f.severity]}>{f.severity}</Badge>
                  {f.user_active === false && <Badge color="bg-slate-800 text-white">suspended</Badge>}
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{(f.reasons || []).slice(0, 2).join(" · ")}</p>
              </div>
              <Icon name="chevron_right" className="text-slate-300" />
            </button>
          ))}
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40">Prev</button>
              <span className="text-sm text-slate-500">Page {data.page} / {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}

      {detailId && <FraudDetailModal id={detailId} onClose={() => setDetailId(null)} onActioned={() => { loadSummary(); load(); }} />}
    </div>
  );
};


const RefundsPage = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const qs = () => {
    const p = new URLSearchParams({ status });
    if (search) p.set("search", search);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p.toString();
  };
  const load = () => adminApi.get(`/refunds?${qs()}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [status, dateFrom, dateTo]);

  const exportCsv = async () => {
    try {
      const r = await adminApi.get(`/refunds/export.csv?${qs()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "refunds.csv"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { window.alert("Export failed"); }
  };

  const review = async (id, action, prefillNote, amount) => {
    let note = prefillNote || "";
    if (action === "reject" && prefillNote === undefined) { note = window.prompt("Reason for rejection (shown to the customer):") || ""; }
    setBusy(id);
    try {
      await adminApi.post(`/refunds/${id}/review`, { action, note, ...(amount != null ? { amount } : {}) });
      load();
    } catch (e) { window.alert(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  const approve = (r) => {
    const input = window.prompt(`Approve refund. Enter amount to refund (max ₦${r.amount}). Leave as-is for full refund:`, String(r.amount));
    if (input === null) return;
    const amt = Number(input);
    if (!amt || amt <= 0 || amt > r.amount) { window.alert(`Amount must be between ₦1 and ₦${r.amount}`); return; }
    review(r.id, "approve", "", amt);
  };

  const SPENT_REASON = "The excess funds have already been spent, so no unused balance remains to refund.";

  return (
    <div className="space-y-5" data-testid="admin-refunds">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="refunds-status-filter" className="border border-slate-200 rounded-xl px-4 py-2.5 outline-none">
          <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="all">All</option>
        </select>
        <div className="flex items-center gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="refunds-search" placeholder="Search name / email / ref" className="border border-slate-200 rounded-xl px-4 py-2.5 outline-none w-56" />
          <button onClick={load} data-testid="refunds-search-btn" className="px-3 py-2.5 bg-slate-100 rounded-xl text-sm font-semibold">Search</button>
        </div>
        <label className="text-xs text-slate-500 flex items-center gap-1">From<input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="refunds-date-from" className="border border-slate-200 rounded-lg px-2 py-2 outline-none" /></label>
        <label className="text-xs text-slate-500 flex items-center gap-1">To<input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="refunds-date-to" className="border border-slate-200 rounded-lg px-2 py-2 outline-none" /></label>
        <button onClick={exportCsv} data-testid="refunds-export-btn" className="px-3 py-2.5 bg-slate-800 text-white rounded-xl text-sm font-semibold flex items-center gap-1"><Icon name="download" className="text-base" />Export CSV</button>
        {data?.summary && <div className="text-sm text-slate-600 ml-auto">Pending: <b>{data.summary.pending?.count || 0}</b> · Approved: <b>{data.summary.approved?.count || 0}</b> · Rejected: <b>{data.summary.rejected?.count || 0}</b></div>}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr><th className="text-left px-5 py-3">Customer</th><th className="text-left px-3 py-3">Amount</th><th className="text-left px-3 py-3">Bank account</th><th className="text-left px-3 py-3">Original ref</th><th className="text-left px-3 py-3">Reason</th><th className="text-left px-3 py-3">Status</th><th className="text-left px-5 py-3">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(!data || data.items.length === 0) && <tr><td colSpan="7" className="px-5 py-8 text-center text-slate-400">No refund requests.</td></tr>}
              {data?.items.map((r) => (
                <tr key={r.id} data-testid={`admin-refund-${r.id}`}>
                  <td className="px-5 py-3"><p className="font-semibold text-slate-900">{r.user_name || "—"}</p><p className="text-xs text-slate-500">{r.user_email}</p></td>
                  <td className="px-3 py-3 font-bold text-slate-900">
                    {naira(r.amount)}
                    {r.status === "pending" && r.funds_spent && (
                      <span data-testid={`refund-flag-${r.id}`} className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 align-middle" title={`Available now: ${naira(r.available_balance || 0)}`}>
                        <Icon name="warning" className="text-xs" /> Funds spent
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-slate-600">{r.bank_name}<br /><span className="text-xs">{r.account_number} · {r.account_name}</span></td>
                  <td className="px-3 py-3 text-xs text-slate-500">{r.original_reference}</td>
                  <td className="px-3 py-3 text-xs text-slate-500 max-w-[160px] truncate">{r.reason || "—"}</td>
                  <td className="px-3 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === "pending" ? "bg-amber-100 text-amber-700" : r.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.status}</span></td>
                  <td className="px-5 py-3">
                    {r.status === "pending" ? (
                      <div className="flex gap-2">
                        {r.funds_spent ? (
                          <button onClick={() => review(r.id, "reject", SPENT_REASON)} disabled={busy === r.id} data-testid={`refund-reject-spent-${r.id}`} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold disabled:opacity-50" title={SPENT_REASON}>Reject · funds spent</button>
                        ) : (
                          <button onClick={() => approve(r)} disabled={busy === r.id} data-testid={`refund-approve-${r.id}`} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold disabled:opacity-50">Approve</button>
                        )}
                        <button onClick={() => review(r.id, "reject")} disabled={busy === r.id} data-testid={`refund-reject-${r.id}`} className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-xs font-bold disabled:opacity-50">Reject</button>
                      </div>
                    ) : <span className="text-xs text-slate-400">{r.admin_note || "—"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


const BroadcastPage = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [audience, setAudience] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [seg, setSeg] = useState({ network: "", min_spend: "", spend_days: 30, activity: "", has_balance: false });
  const [savedSegs, setSavedSegs] = useState([]);

  const loadSegs = () => adminApi.get("/broadcast/segments").then((r) => setSavedSegs(r.data.segments || [])).catch(() => {});
  useEffect(() => { loadSegs(); }, []);

  const applySaved = (id) => {
    const s = savedSegs.find((x) => x.id === id);
    if (!s) return;
    const sg2 = s.segment || {};
    setSeg({ network: sg2.network || "", min_spend: sg2.min_spend || "", spend_days: sg2.spend_days || 30, activity: sg2.activity || "", has_balance: !!sg2.has_balance });
  };
  const saveSeg = async () => {
    const name = window.prompt("Name this audience (e.g. MTN big spenders):");
    if (!name || !name.trim()) return;
    await adminApi.post("/broadcast/segments", { name: name.trim(), segment: segBody() });
    loadSegs();
  };
  const deleteSeg = async (id) => {
    if (!window.confirm("Delete this saved audience?")) return;
    await adminApi.delete(`/broadcast/segments/${id}`);
    loadSegs();
  };

  const segParams = () => {
    const p = {};
    if (seg.network) p.network = seg.network;
    if (seg.min_spend) { p.min_spend = seg.min_spend; p.spend_days = seg.spend_days; }
    if (seg.activity) p.activity = seg.activity;
    if (seg.has_balance) p.has_balance = true;
    return p;
  };
  const segBody = () => {
    const b = {};
    if (seg.network) b.network = seg.network;
    if (seg.min_spend) { b.min_spend = Number(seg.min_spend); b.spend_days = Number(seg.spend_days); }
    if (seg.activity) b.activity = seg.activity;
    if (seg.has_balance) b.has_balance = true;
    return b;
  };

  useEffect(() => {
    const t = setTimeout(() => {
      adminApi.get("/broadcast/audience", { params: segParams() }).then((r) => setAudience(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [seg]);

  const send = async () => {
    if (!title.trim() || !message.trim()) { setResult({ ok: false, text: "Title and message are required." }); return; }
    if (!window.confirm(`Send this promotion to ${audience?.opted_in ?? "all matching"} customers?`)) return;
    setSending(true); setResult(null);
    try {
      const r = await adminApi.post("/broadcast", { title: title.trim(), message: message.trim(), link: link.trim(), send_email: sendEmail, segment: segBody() });
      setResult({ ok: true, text: `Sent to ${r.data.sent} customer${r.data.sent === 1 ? "" : "s"}.` });
      setTitle(""); setMessage(""); setLink("");
    } catch (e) {
      setResult({ ok: false, text: e.response?.data?.detail || "Failed to send broadcast" });
    } finally { setSending(false); }
  };

  const sg = (k) => (e) => setSeg((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="admin-broadcast">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div>
          <p className="text-sm font-bold text-slate-700">Send a promotion</p>
          <p className="text-xs text-slate-500 mt-1">
            Targets opted-in customers matching the filters below
            {audience && <> — <span className="font-semibold text-slate-700">{audience.opted_in}</span> match, <span className="font-semibold text-slate-700">{audience.with_push}</span> with a push device</>}.
            Delivered as an in-app message + push; email is optional.
          </p>
        </div>

        {/* Segment targeting */}
        <div className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid="broadcast-segment">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Audience filters (combined)</p>
            <button type="button" onClick={saveSeg} data-testid="segment-save" className="text-xs font-semibold text-red-600 flex items-center gap-1"><Icon name="bookmark_add" className="text-sm" /> Save audience</button>
          </div>
          {savedSegs.length > 0 && (
            <div className="flex items-center gap-2" data-testid="saved-segments">
              <select onChange={(e) => { if (e.target.value) applySaved(e.target.value); }} value="" data-testid="segment-load" className={inputCls}>
                <option value="">Load a saved audience…</option>
                {savedSegs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select onChange={(e) => { if (e.target.value) deleteSeg(e.target.value); }} value="" data-testid="segment-delete" className="h-11 rounded-xl border border-slate-200 px-2 text-sm text-slate-500 shrink-0" title="Delete a saved audience">
                <option value="">🗑</option>
                {savedSegs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Network</label>
              <select value={seg.network} onChange={sg("network")} data-testid="seg-network" className={inputCls}>
                <option value="">Any</option>
                {["MTN", "Airtel", "Glo", "9mobile"].map((n) => <option key={n} value={n}>{n} users</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Activity</label>
              <select value={seg.activity} onChange={sg("activity")} data-testid="seg-activity" className={inputCls}>
                <option value="">Any</option>
                <option value="active">Active (last 30 days)</option>
                <option value="dormant">Dormant (30+ days)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Min spend (₦)</label>
              <input type="number" value={seg.min_spend} onChange={sg("min_spend")} data-testid="seg-min-spend" className={inputCls} placeholder="e.g. 5000" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Spend window</label>
              <select value={seg.spend_days} onChange={sg("spend_days")} data-testid="seg-spend-days" className={inputCls}>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 12 months</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="seg-has-balance">
            <input type="checkbox" checked={seg.has_balance} onChange={sg("has_balance")} className="w-4 h-4 accent-red-600" />
            <span className="text-sm font-semibold text-slate-700">Only customers with wallet balance</span>
          </label>
        </div>

        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} data-testid="broadcast-title" className={inputCls} placeholder="e.g. 10% off all data this weekend 🎉" />
        </Field>
        <Field label="Message">
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={3} data-testid="broadcast-message" className={inputCls} placeholder="Short, punchy announcement customers will love." />
        </Field>
        <Field label="Link (optional — opens when tapped)">
          <input value={link} onChange={(e) => setLink(e.target.value)} maxLength={300} data-testid="broadcast-link" className={inputCls} placeholder="/discover or https://…" />
        </Field>
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="broadcast-email-toggle">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4 accent-red-600" />
          <span className="text-sm font-semibold text-slate-700">Also send an email</span>
        </label>
        {result && (
          <div className={`text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${result.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="broadcast-result">
            <Icon name={result.ok ? "check_circle" : "error"} className="text-base" />{result.text}
          </div>
        )}
        <button onClick={send} disabled={sending || !title.trim() || !message.trim()} data-testid="broadcast-send"
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
          <Icon name={sending ? "progress_activity" : "send"} className={sending ? "animate-spin" : ""} />{sending ? "Sending…" : `Send to ${audience?.opted_in ?? "…"} customers`}
        </button>
      </div>

      {/* Live preview */}
      <div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Preview</p>
        <div className="bg-slate-900 rounded-2xl p-4 flex items-start gap-3 max-w-sm" data-testid="broadcast-preview">
          <div className="w-10 h-10 rounded-xl bg-red-600/20 flex items-center justify-center shrink-0"><Icon name="campaign" className="text-red-400" /></div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">{title || "Your title appears here"}</p>
            <p className="text-white/60 text-xs mt-0.5 line-clamp-3">{message || "Your message preview will show here as customers will see it."}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PromotionsPage = () => {
  const [tab, setTab] = useState("broadcast");
  const tabs = [{ id: "broadcast", label: "Broadcast", icon: "campaign" }, { id: "discounts", label: "Discounts", icon: "sell" }];
  return (
    <div data-testid="admin-promotions">
      <div className="flex gap-2 mb-5">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`promotions-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === t.id ? "bg-red-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            <Icon name={t.icon} className="text-base" />{t.label}
          </button>
        ))}
      </div>
      {tab === "broadcast" ? <BroadcastPage /> : <DiscountsPage />}
    </div>
  );
};

const NAV = [
  { id: "overview", label: "Overview", icon: "dashboard" },
  { id: "customers", label: "Customers", icon: "group" },
  { id: "orders", label: "Orders", icon: "shopping_cart" },
  { id: "transactions", label: "Transactions", icon: "receipt_long" },
  { id: "products", label: "Products", icon: "inventory_2" },
  { id: "referral", label: "Referral", icon: "diversity_3" },
  { id: "promotions", label: "Promotions", icon: "campaign" },
  { id: "rewards", label: "Cashback", icon: "redeem" },
  { id: "content", label: "Content", icon: "edit_note" },
  { id: "offers", label: "Offers", icon: "local_activity" },
  { id: "partners", label: "Partners", icon: "handshake" },
  { id: "enterprises", label: "Enterprises", icon: "corporate_fare" },
  { id: "commissions", label: "Commissions", icon: "payments" },
  { id: "refunds", label: "Refunds", icon: "currency_exchange" },
  { id: "fraud", label: "Fraud & Risk", icon: "gpp_maybe" },
  { id: "security", label: "Security", icon: "shield" },
  { id: "audit", label: "Audit Log", icon: "history" },
  { id: "api", label: "API Monitor", icon: "monitoring" },
];

const PAGES = {
  overview: OverviewPage, customers: CustomersPage, orders: OrdersPage, transactions: TransactionsPage,
  products: ProductsPage, referral: ReferralPage, promotions: PromotionsPage, rewards: RewardsPage, content: ContentPage, offers: OffersPage, partners: PartnersPage, enterprises: EnterprisesPage, commissions: CommissionsPage, refunds: RefundsPage, fraud: FraudPage, security: SecurityPage, audit: AuditLogPage, api: ApiMonitorPage,
};

const AdminLayout = ({ admin, onLogout }) => {
  const [view, setView] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const Page = PAGES[view];
  const label = NAV.find((n) => n.id === view)?.label;
  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#0B1220] text-white flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-5 border-b border-white/10">
          <img src={APP_LOGO_WHITE} alt="TopIt" className="h-7 w-auto object-contain" data-testid="admin-brand-icon" />
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="text-red-500 font-bold text-[11px] uppercase tracking-wide">Admin</span>
            <span className="text-slate-500 text-[9px] tracking-wide" data-testid="powered-by-sineli">· Powered by Sineli</span>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => { setView(n.id); setMobileOpen(false); }} data-testid={`nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${view === n.id ? "bg-red-600 text-white" : "text-slate-300 hover:bg-white/5"}`}>
              <Icon name={n.icon} className="text-lg" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="px-3 py-2 text-xs text-slate-400">{admin?.email}</div>
          <button onClick={onLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:bg-white/5">
            <Icon name="logout" className="text-lg" /> Logout
          </button>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3 sticky top-0 z-20">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"><Icon name="menu" /></button>
          <h1 className="text-xl font-bold text-slate-900" data-testid="page-title">{label}</h1>
          <a href="/" className="ml-auto text-sm text-slate-500 hover:text-red-600 flex items-center gap-1"><Icon name="open_in_new" className="text-base" /> View app</a>
        </header>
        <main className="flex-1 p-6 overflow-x-hidden">
          <Page />
        </main>
      </div>
    </div>
  );
};

// ==================== ROOT ====================
const AdminApp = () => {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Session lives in an httpOnly cookie — validate by calling /me
    adminApi.get("/me").then((r) => setAdmin(r.data)).catch(() => {}).finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try { await adminApi.post("/logout"); } catch { /* noop */ }
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Icon name="progress_activity" className="text-red-600 text-5xl animate-spin" /></div>;
  if (!admin) return <AdminLogin onLogin={setAdmin} />;
  return <AdminLayout admin={admin} onLogout={logout} />;
};

export default AdminApp;
